import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { CSVLink } from "react-csv";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./App.css";
import { FaMoon, FaSun } from "react-icons/fa";
import { FaGlobe } from "react-icons/fa";
import Wowowl from "./assets/wowowl-logo.gif"; // Import your logo
import creatorImg from "./assets/Aniket.png";
import emailjs from "@emailjs/browser";
import { useMemo } from "react";
import "./EditorialTheme.css";
import ErrorBoundary from "./ErrorBoundary";
import blogPosts from "./blogPosts.json";
import { trackEvent, hostnameOf } from "./analytics";
import { pagesScanned, pagesWithMatches } from "./scanStats";
import { lsGet, lsSet, lsRemove, lsGetJSON } from "./safeStorage";
import { parseTimestamp } from "./timestamps";
import {
  toProblem, problemFromResponse, problemPlacement, hostOf, looksLikeWebAddress, suggestAddress,
  statusLabel, unreachableReason, scopeList,
} from "./scanMessages";
import { csvSafe } from "./csvSafe";

// Loaded on demand: the cursor effect after the visitor's first mouse/touch
// input, the PDF library (jsPDF, ~420 KB) when PDF is clicked. Both used to
// be in the main bundle every visitor downloads and parses up front.
const SplashCursor = React.lazy(() => import("./SplashCursor"));
const loadPdfGenerator = () => import("./generatePDF").then((m) => m.generatePDF);

// Guest search history lives under this key. Signed-in history comes from the
// backend and is never written to the device. The old "searchHistory" key
// also collected account history (it synced whatever list was on screen), so
// it's discarded on load rather than shown to whoever uses the device next.
const GUEST_HISTORY_KEY = "wowowl_guest_history";
const LEGACY_HISTORY_KEY = "searchHistory";

// Smart Search scans at most this many keywords in total (seeds + related
// words) — the backend's kw_limit for Smart Search in /api/scan.
const SMART_SEARCH_MAX_KEYWORDS = 10;

// Seeds first, then each seed's selected related words taken round-robin, so
// every seed gets a fair share of the budget instead of the first seed's six
// suggestions using it all up.
function smartSearchWords(seedWords, expansions) {
  const out = [];
  const seen = new Set();
  const add = (w) => {
    const key = w.toLowerCase();
    if (out.length >= SMART_SEARCH_MAX_KEYWORDS || seen.has(key)) return;
    seen.add(key);
    out.push(w);
  };
  seedWords.forEach(add);
  const lists = seedWords.map(w =>
    (expansions[w]?.suggestions || []).filter(s => s.selected).map(s => s.word)
  );
  const longest = Math.max(0, ...lists.map(l => l.length));
  for (let i = 0; i < longest; i++) lists.forEach(l => { if (l[i]) add(l[i]); });
  return out;
}

// Keywords as typed vs. as searched. A keyword wrapped in double quotes
// ("Mumbai, India") is one exact phrase: the quotes are dropped before
// scanning. An unquoted keyword containing a comma could be a list or a phrase,
// so the form asks (commaChoiceIndex) instead of guessing.
const QUOTED_KEYWORD = /^["\u201C\u201D]([\s\S]*)["\u201C\u201D]$/;
function normalizeKeyword(w) {
  const t = (w || "").trim();
  const m = t.match(QUOTED_KEYWORD);
  return m ? m[1].trim() : t;
}
function commaParts(w) {
  const t = (w || "").trim();
  if (QUOTED_KEYWORD.test(t)) return [];
  const parts = t.split(",").map(p => p.trim()).filter(Boolean);
  return parts.length > 1 ? parts : [];
}

// Session lives in an HttpOnly cookie now, not a token we hold in JS —
// this makes the browser send/accept it on every request to the API.
axios.defaults.withCredentials = true;

// ── Theme ───────────────────────────────────────────────────────────────────
// The editorial theme is the only one. The old "classic" look and its
// ?ui=classic switch were removed on 2026-10-05; see CLAUDE.md.

// Localized pricing display — pricingTable comes from GET /api/pricing,
// which mirrors Dodo's own country-override config live (see app.py's
// _build_pricing_table). No hardcoded per-country table here — adding a
// country in the Dodo dashboard shows up automatically, no code change.
// planKey is "pro" | "value" | "starter". `amount` from Dodo is in the
// smallest currency unit (Stripe-style, e.g. cents) — converted to major
// units via Intl.NumberFormat's own resolved fraction-digit count for that
// currency, rather than a hardcoded "USD/EUR = 2 decimals" assumption, so an
// unusual currency (0 decimals like JPY, 3 like KWD) still formats correctly
// with zero code changes here either.
function getLocalizedAmountParts(pricingTable, countryCode, planKey) {
  const entry = pricingTable?.[countryCode]?.[planKey];
  if (!entry) return null;
  try {
    const fmt = new Intl.NumberFormat(undefined, { style: "currency", currency: entry.currency });
    const fractionDigits = fmt.resolvedOptions().maximumFractionDigits;
    const majorValue = entry.amount / Math.pow(10, fractionDigits);
    return { fmt, majorValue };
  } catch (e) {
    return null; // unrecognized/invalid currency code — fall back to ₹
  }
}

// Formatted display string, e.g. "$5.00", or null to fall back to the plain
// ₹-denominated price.
function getLocalizedPrice(pricingTable, countryCode, planKey) {
  const parts = getLocalizedAmountParts(pricingTable, countryCode, planKey);
  return parts ? parts.fmt.format(parts.majorValue) : null;
}

// Same, but divided by `units` first — for a per-scan rate like "$0.40/scan".
function getLocalizedPricePerUnit(pricingTable, countryCode, planKey, units) {
  const parts = getLocalizedAmountParts(pricingTable, countryCode, planKey);
  return parts ? `${parts.fmt.format(parts.majorValue / units)}/scan` : null;
}

// Free has no Dodo product of its own, so there's no pricingTable[countryCode].free
// entry to read a currency from directly — but every paid plan for that country
// shares the same currency, so borrow whichever one exists just to render a
// correctly-localized "0" (e.g. "$0.00" next to an already-localized "$2.00", not a
// hardcoded "₹0" sitting next to it, which is exactly what looked broken/inconsistent
// before this fix).
function getLocalizedFreeLabel(pricingTable, countryCode) {
  const entry = pricingTable?.[countryCode];
  const currency = entry?.starter?.currency || entry?.value?.currency || entry?.pro?.currency;
  if (!currency) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(0);
  } catch (e) {
    return null; // unrecognized/invalid currency code — fall back to ₹0
  }
}

// Shared ambient-typing phrase pool — both classic and editorial heroes use
// this. Expanded from the original 6 to 14 on 2026-07-03.
const phrasesList = [
  "How to search a word in website?",
  "Scan your site for keyword usage",
  "Find exact matches across pages",
  "WowOwl helps SEO pros succeed",
  "Lightning-fast, accurate search",
  "WowOwl makes scanning effortless",
  "Crawl every page in seconds",
  "Track keywords across your domain",
  "Compare 3 sites side by side",
  "Schedule scans, get email alerts",
  "Export results to CSV or PDF",
  "Bulk scan hundreds of URLs",
  "Find what Ctrl-F can't",
  "Site-wide keyword intelligence",
];

// Shared between classic and editorial — same cards, same order. On desktop
// this renders as the existing 3/2-column grid; below 600px it becomes a
// swipeable slider (see .features-grid mobile rules in App.css). Pulled into
// an array (was 9 near-identical inline JSX blocks) so the slider's active
// dot and scroll-to-card refs can be indexed instead of duplicated by hand.
const featureItems = [
  { icon: "📊", title: "CSV & PDF Export", desc: "Download scan results for offline use and reporting." },
  { icon: "⇄", title: "Compare Websites", desc: "Scan up to 3 sites side-by-side and spot keyword gaps instantly." },
  { icon: "🕵️‍♂️", title: "Strict Match", desc: "Enable whole-word matching for precise analysis." },
  { icon: "📁", title: "Search History", desc: "Sign in to sync your scan history across devices and revisit it any time." },
  { icon: "📋", title: "Bulk URL Scan", desc: "Paste or upload a list of URLs and scan them all in one run." },
  { icon: "🗓️", title: "Scheduled Scans", desc: "Set up recurring daily or weekly scans, with results sent straight to your inbox." },
  { icon: "📧", title: "Email Results", desc: "Send scan results straight to your inbox — no download required." },
  { icon: "🔢", title: "Multi-Keyword Search", desc: "Track up to 5 keywords in a single scan." },
  { icon: "🧠", title: "Smart Search", desc: "Auto-expands keywords with synonyms and variants — e.g. price → pricing, cost, value." },
];

// One per family from the earthy/muted swatch set worked out for the light
// theme's splash cursor (terracotta, camel, deep rust, etc. were all drawn
// from this same set). Picked once per page load — see lightSplashColor
// below — rather than per splat like SplashCursor's own RAINBOW_MODE, so
// the color stays consistent for the whole visit instead of shifting mid-use.
const LIGHT_SPLASH_COLORS = [
  "#B8542E", // terracotta
  "#C99A3E", // warm gold
  "#C97B8C", // dusty rose
  "#8A9A5C", // sage green
  "#3C8C8A", // dusty teal
  "#5C7A99", // dusty blue
  "#965A3C", // sienna
  "#9A5A7A", // muted plum
  "#C96A2E", // burnt orange
  "#A8452E", // deep rust
  "#A8A85C", // dusty chartreuse
  "#B08D5C", // camel
  "#B87333", // copper
];



// ── Corrected floating-phrase effect for the editorial hero ─────────────────
// Fixes three bugs found in the earlier (classic hero) version of this effect: (1) the typing animation's end-width was a hardcoded
// 27ch, while every phrase in phrasesList is 30-34 characters — so classic
// mode has never once revealed a full phrase, always truncating a few
// characters short; (2) FloatingPhrase carried its own self-remount timer,
// but the parent unconditionally removes it after a flat 3500ms — shorter
// than that internal timer for every phrase here — so the remount logic was
// dead code, and longer phrases got yanked mid-fade instead of finishing
// cleanly; (3) phrases were chosen via plain Math.random() with no check
// against what's currently on screen, so the same phrase could double up.
export function EditorialFloatingPhrase({ text, position }) {
  const charCount = text.length;
  const typingDuration = Math.max(0.6, (200 + charCount * 60) / 1000);
  const holdThenFadeAt = typingDuration + 0.6;

  return (
    <span
      className="editorial-typing-phrase"
      style={{
        position: "absolute",
        top: position.top,
        left: position.left,
        "--phrase-ch": `${charCount}ch`,
        animation: `editorial-typing ${typingDuration}s steps(${charCount}, end) forwards, editorial-fadeout 0.8s ease ${holdThenFadeAt}s forwards`,
      }}
    >
      {text}
    </span>
  );
}

export function EditorialFloatingPhraseCloud({ excludeRefs = [] }) {
  const [phrases, setPhrases] = useState([]);
  const activeTextsRef = useRef(new Set());
  const positionsRef = useRef([]);
  const MAX_PHRASES = 6;
  const MIN_DISTANCE = 90;

  // Was only checking whether the phrase's single top-left anchor point fell
  // inside an excluded rect — never accounted for the phrase's actual
  // rendered width, so a long phrase could start just outside the headline's
  // box and still visually extend right over it. Now does a proper
  // rectangle-vs-rectangle overlap test using the phrase's estimated width.
  const isOverlappingExcluded = useCallback((top, left, charCount) => {
    const phraseLeftPx = (left / 100) * window.innerWidth;
    const phraseTopPx = (top / 100) * window.innerHeight;
    const phraseRightPx = phraseLeftPx + charCount * 7;
    const phraseBottomPx = phraseTopPx + 20;
    const buffer = 16;

    for (const ref of excludeRefs) {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) continue;
      const overlaps =
        phraseLeftPx < rect.right + buffer &&
        phraseRightPx > rect.left - buffer &&
        phraseTopPx < rect.bottom + buffer &&
        phraseBottomPx > rect.top - buffer;
      if (overlaps) return true;
    }
    return false;
  }, [excludeRefs]);

  // Re-added per request — phrases had been allowed to overlap each other
  // freely; now kept at least MIN_DISTANCE apart again, same approach as
  // classic mode's version.
  const isFarEnough = (top, left) => positionsRef.current.every((pos) => {
    const dx = (left - pos.left) * window.innerWidth / 100;
    const dy = (top - pos.top) * window.innerHeight / 100;
    return Math.sqrt(dx * dx + dy * dy) > MIN_DISTANCE;
  });

  const getSafePosition = useCallback((charCount) => {
    let tries = 0;
    let top, left;
    const bannerHeight = window.innerHeight;
    const bannerWidth = window.innerWidth * 0.95;

    do {
      top = Math.floor(Math.random() * 70) + 8;
      left = Math.floor(Math.random() * 75) + 8;
      const pxTop = (top / 100) * window.innerHeight;
      const pxLeft = (left / 100) * window.innerWidth;
      const estimatedWidth = charCount * 7;
      const estimatedHeight = 20;
      if (pxTop + estimatedHeight > bannerHeight || pxLeft + estimatedWidth > bannerWidth) { tries++; continue; }
      tries++;
    } while ((isOverlappingExcluded(top, left, charCount) || !isFarEnough(top, left)) && tries < 20);

    positionsRef.current.push({ top, left });
    if (positionsRef.current.length > MAX_PHRASES) positionsRef.current.shift();
    return { top: `${top}%`, left: `${left}%` };
  }, [isOverlappingExcluded]);

  useEffect(() => {
    // Spawns roughly every 700ms now (was 1800ms) — combined with each
    // phrase's ~2-4s lifetime, that keeps around 4-6 visible at once instead
    // of ~2, and with no inter-phrase spacing check they naturally cluster.
    const interval = setInterval(() => {
      const available = phrasesList.filter((p) => !activeTextsRef.current.has(p));
      const pool = available.length > 0 ? available : phrasesList;
      const phrase = pool[Math.floor(Math.random() * pool.length)];
      const position = getSafePosition(phrase.length);
      const id = Math.random();

      const typingDuration = Math.max(0.6, (200 + phrase.length * 60) / 1000);
      const totalDurationMs = (typingDuration + 0.6 + 0.8) * 1000; // typing + hold + fade

      activeTextsRef.current.add(phrase);
      setPhrases((prev) => [...prev, { id, text: phrase, position }]);

      setTimeout(() => {
        activeTextsRef.current.delete(phrase);
        setPhrases((prev) => prev.filter((p) => p.id !== id));
      }, totalDurationMs);
    }, 700);

    return () => clearInterval(interval);
  }, [getSafePosition]);

  return (
    <>
      {phrases.map((p) => (
        <EditorialFloatingPhrase key={p.id} text={p.text} position={p.position} />
      ))}
    </>
  );
}

export function HeroBanner({ onScrollDown }) {
  const editorialEyebrowRef = useRef(null);
  const editorialH1Ref = useRef(null);
  const editorialSubRef = useRef(null);

  return (
      <section id="banner" className="hero-banner hero-banner-editorial position-relative">
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <p className="hero-eyebrow" ref={editorialEyebrowRef}>
            <span className="hero-eyebrow-dot">◆</span> Site-wide keyword scanner
          </p>
          <h1 className="hero-editorial-h1" ref={editorialH1Ref}>
            Find any <span className="hero-word-highlight">word</span> buried across every page.
          </h1>
          <p className="hero-editorial-sub" ref={editorialSubRef}>
            Ctrl-F stops at one page. WowOwl crawls the whole domain and counts
            every match, everywhere — for SEO pros, content teams, and researchers.
          </p>
        </div>
        <EditorialFloatingPhraseCloud excludeRefs={[editorialEyebrowRef, editorialH1Ref, editorialSubRef]} />
        <div className="scroll-wrapper hero-scroll-wrapper" onClick={onScrollDown}>
          <button className="scroll-down-button" aria-label="Scroll down">
            <span></span>
          </button>
        </div>
      </section>
  );
}

function Header({ darkMode, setDarkMode, user, onLogin, onLogout, onManageBilling }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  return (
    <header className="header">
      <div className="logo-container">
        <img src={Wowowl} alt="WebApp Logo" className="app-logo" />
        <h1 className="app-title">WowOwl</h1>
      </div>

      {/* ✅ Navigation Menu */}
      <nav className={`top-nav ${menuOpen ? "open" : ""}`}>
        <a href="#scan" onClick={() => setMenuOpen(false)}>
          Scan
        </a>
        <a href="#features" onClick={() => setMenuOpen(false)}>
          Features
        </a>
        <a href="#about" onClick={() => setMenuOpen(false)}>
          About
        </a>
        <a href="#faqs" onClick={() => setMenuOpen(false)}>
          FAQs
        </a>
        <a href="#blog" onClick={() => setMenuOpen(false)}>
          Blog
        </a>
        {user?.plan !== "pro" && (
          <a href="#pricing" onClick={() => setMenuOpen(false)}>
            Pricing
          </a>
        )}
        <a href="#contact" onClick={() => setMenuOpen(false)}>
          Contact
        </a>

        {/* Mobile-only — the header's toggle-container is hidden below 768px
            (see App.css) so this is the only way to reach dark mode on
            phones; it's a menu row here instead of a floating header icon. */}
        <div className="nav-theme-row">
          <span className="nav-theme-label">
            {darkMode
              ? <FaMoon style={{ fontSize: "15px", flexShrink: 0 }} />
              : <FaSun style={{ fontSize: "15px", flexShrink: 0 }} />}
            {darkMode ? "Dark mode" : "Light mode"}
          </span>
          <div
            className={`exact-match-toggle ${darkMode ? "active" : ""}`}
            onClick={() => setDarkMode((prev) => !prev)}
            role="switch"
            aria-checked={darkMode}
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") { e.preventDefault(); setDarkMode((prev) => !prev); }
            }}
          >
            <span className="exact-match-knob" />
          </div>
        </div>
      </nav>

      {/* ✅ Toggle, Auth & Hamburger */}
      <div className="d-flex align-items-center gap-2">
        <div className="toggle-container me-1">
          <input
            type="checkbox"
            id="toggle"
            checked={darkMode}
            onChange={() => setDarkMode((prev) => !prev)}
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          />
          <label htmlFor="toggle" className="d-flex align-items-center gap-2">
            <FaSun className="sun-icon" />
            <FaMoon className="moon-icon" />
          </label>
        </div>

        {/* Auth: sign-in button or user avatar */}
        {user ? (
          <div className="user-menu-wrapper" style={{ position: "relative" }}>
            <button
              className="user-avatar-btn"
              onClick={() => setUserMenuOpen(prev => !prev)}
              title={user.name}
            >
              {user.picture
                ? <img src={user.picture} alt={user.name} className="user-avatar-img" />
                : <span className="user-avatar-initials">{user.name.charAt(0).toUpperCase()}</span>
              }
            </button>
            {userMenuOpen && (
              <div className="user-dropdown">
                <div className="user-dropdown-name">{user.name}</div>
                <div className="user-dropdown-email">{user.email}</div>
                <div className={`plan-badge ${user.plan === "pro" ? "pro" : "free"}`}>
                  {user.plan === "pro" ? "Pro ✓" : "Free plan"}
                </div>
                {/* Also for past Pro customers: one whose renewal failed is moved to
                    Free after the grace period and pays the renewal from Dodo's portal. */}
                {(user.plan === "pro" || user.billing_portal) && (
                  <button
                    className="user-dropdown-billing"
                    onClick={() => { setUserMenuOpen(false); onManageBilling(); }}
                  >
                    {user.subscription_status === "on_hold" ? "Pay renewal & restore Pro" : "Billing & invoices"}
                  </button>
                )}
                <hr style={{ margin: "6px 0" }} />
                <button className="user-dropdown-signout" onClick={() => { setUserMenuOpen(false); onLogout(); }}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        ) : (
          <button className="google-signin-btn" onClick={onLogin}>
            <FaGlobe style={{ fontSize: "13px" }} />
            Sign in
          </button>
        )}

        {/* Hamburger */}
        <div className="hamburger" onClick={() => setMenuOpen((prev) => !prev)}>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </header>
  );
}

export function ContactForm() {
  const form = useRef();
  const [sent, setSent] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();

    const userEmail = form.current.user_email.value;
    const userName = form.current.user_name.value;

    emailjs
      .sendForm(
        "service_ybxiqzv",
        "template_zx3wfdz",
        form.current,
        "X-ZzJ_YyEhZvbI2Wf"
      )
      .then(() => {
        return emailjs.send(
          "service_ybxiqzv",
          "template_423v2di",
          {
            user_name: userName,
            user_email: userEmail,
          },
          "X-ZzJ_YyEhZvbI2Wf"
        );
      })
      .then(() => {
        setSent(true);
        form.current.reset(); // 🧹 Clear form

        setTimeout(() => {
          setSent(false); // ⏱ Auto-hide after 5 sec
        }, 5000);
      })
      .catch((error) => {
        console.error("Email sending failed:", error);
        alert("Something went wrong. Please try again.");
      });
  };

  return (
    <section className="contact-section container my-5">
      <div className="row align-items-start">
        {/* Left: Text content */}
        <div className="col-md-6">
          <p className="contact-subtitle">We’d Love to Hear from You</p>
          <p>
            Whether you have a suggestion, found an issue, or just want to say
            hello — drop us a message using the form. We value your feedback and
            are always looking to improve WowOwl.
          </p>
          <p>
            Fill out the form and we’ll get back to you as soon as possible.
          </p>
        </div>

        {/* Right: Contact Form */}
        <div className="col-md-6">
          <form ref={form} onSubmit={sendEmail}>
            <div className="mb-3">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="user_name"
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="user_email"
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="reason" className="form-label">
                Reason for Contact
              </label>
              <select
                className="form-select"
                name="reason"
                id="reason"
                required
              >
                <option value="">What it is?</option>
                <option value="Suggestion">Suggestion</option>
                <option value="Issue">Issue</option>
                <option value="General">General</option>
              </select>
            </div>
            <div className="mb-3">
              <label htmlFor="message" className="form-label">
                Message
              </label>
              <textarea
                className="form-control"
                id="message"
                name="message"
                rows="4"
                required
              ></textarea>
            </div>
            <button type="submit" className="btn btn-dark">
              Send Message
            </button>
          </form>

          {/* ✅ Success Message */}
          {sent && (
            <div className="alert alert-success mt-3" role="alert">
              ✅ Thank you! We've received your message and will be in touch
              shortly.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ScheduleModal({ user, onClose, onUpgrade }) {
  const [urls, setSchedUrls] = React.useState([""]);
  const [keywordInput, setKeywordInput] = React.useState("");
  const [keywords, setKeywords] = React.useState([]);
  const [frequency, setFrequency] = React.useState("weekly");
  const [email, setEmail] = React.useState(user?.email || "");
  const [saving, setSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  const [schedules, setSchedules] = React.useState(null);

  React.useEffect(() => {
    if (!user) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/schedules`)
      .then(r => setSchedules(r.data)).catch(() => setSchedules([]));
  }, [user]);

  const addKeyword = (e) => {
    if (e) e.preventDefault();
    const kw = keywordInput.trim();
    if (!kw || keywords.length >= 5 || keywords.includes(kw)) return;
    setKeywords(prev => [...prev, kw]);
    setKeywordInput("");
  };

  const handleSave = async () => {
    const validUrls = urls.map(u => u.trim()).filter(Boolean);
    if (!validUrls.length) { setFeedback({ type: "error", msg: "Enter at least one URL." }); return; }
    if (!keywords.length) { setFeedback({ type: "error", msg: "Add at least one keyword." }); return; }
    setSaving(true);
    setFeedback(null);
    try {
      for (const u of validUrls) {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/schedules`, {
          url: u, keywords, frequency, notify_email: email
        });
      }
      setFeedback({ type: "success", msg: `${validUrls.length > 1 ? validUrls.length + " schedules" : "Schedule"} saved!` });
      trackEvent("schedule_created", { schedule_count: validUrls.length, frequency });
      setSchedUrls([""]);
      setKeywords([]);
      const r = await axios.get(`${process.env.REACT_APP_API_URL}/api/schedules`);
      setSchedules(r.data);
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === "limit_reached" && data?.is_pro) {
        // Already Pro — an "Upgrade to Pro" popup here made no sense.
        setFeedback({ type: "error", msg: `You've reached the limit of ${data.limit} active schedules. Pause or delete one to add another.` });
      } else if (data?.error === "limit_reached") {
        onUpgrade();
      } else {
        setFeedback({ type: "error", msg: data?.error || "Failed to save." });
      }
      // Some URLs in a multi-URL save may already have succeeded — show them.
      axios.get(`${process.env.REACT_APP_API_URL}/api/schedules`).then(r => setSchedules(r.data)).catch(() => {});
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    await axios.delete(`${process.env.REACT_APP_API_URL}/api/schedules/${id}`);
    setSchedules(s => s.filter(x => x.id !== id));
  };

  const handleToggle = async (id) => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/schedules/${id}/toggle`, {});
      setSchedules(s => s.map(x => x.id === id ? { ...x, active: res.data.active } : x));
    } catch (err) {
      const data = err.response?.data;
      setFeedback({
        type: "error",
        msg: data?.error === "limit_reached"
          ? (data.is_pro
              ? `You already have ${data.limit} active schedules. Pause another one first.`
              : "Scheduled scans are a Pro feature.")
          : (data?.error || "Couldn't update this schedule."),
      });
    }
  };

  const inp = { width: "100%", padding: "8px 11px", borderRadius: "8px", border: "1px solid var(--border, #ddd)",
    fontSize: "13px", boxSizing: "border-box", color: "var(--ink, #333)", background: "var(--card, #fff)", outline: "none" };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal schedule-modal" onClick={e => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>×</button>
        <div className="upgrade-modal-icon">🗓️</div>
        <h2 className="upgrade-modal-title" style={{ marginBottom: "16px" }}>Schedule Recurring Scan</h2>

        {!user ? (
          <p style={{ color: "#e53935", fontSize: "13px" }}>Please sign in to set up scheduled scans.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>

            {/* URLs */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--muted, #888)", letterSpacing: "0.05em", display: "block", marginBottom: "5px" }}>WEBSITE URL(S)</label>
              {urls.map((u, i) => (
                <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "6px" }}>
                  <input type="url" value={u} onChange={e => { const a = [...urls]; a[i] = e.target.value; setSchedUrls(a); }}
                    placeholder="https://example.com" style={{ ...inp, flex: 1 }} />
                  {urls.length > 1 && (
                    <button type="button" onClick={() => setSchedUrls(urls.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", color: "#e53935", cursor: "pointer", fontSize: "16px", padding: "0 4px" }}>✕</button>
                  )}
                </div>
              ))}
              {urls.length < 3 && (
                <button type="button" onClick={() => setSchedUrls([...urls, ""])}
                  style={{ background: "none", border: "none", color: "var(--accent, #4caf50)", fontSize: "12px", cursor: "pointer", padding: "0", fontWeight: "600" }}>
                  + Add another URL
                </button>
              )}
            </div>

            {/* Keywords */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--muted, #888)", letterSpacing: "0.05em", display: "block", marginBottom: "5px" }}>KEYWORDS</label>
              <form onSubmit={addKeyword} style={{ display: "flex", gap: "6px" }}>
                <input type="text" value={keywordInput}
                  onChange={e => setKeywordInput(e.target.value)}
                  placeholder="Type keyword, press Enter or + Add"
                  style={{ ...inp, flex: 1 }} />
                <button type="button"
                  onClick={addKeyword}
                  style={{ padding: "8px 12px", borderRadius: "8px", border: "2px dashed var(--accent, #4caf50)",
                    background: "none", color: "var(--accent, #4caf50)", fontWeight: "600", fontSize: "12px", cursor: "pointer", whiteSpace: "nowrap" }}>
                  + Add
                </button>
              </form>
              {keywords.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginTop: "7px" }}>
                  {keywords.map(kw => (
                    <span key={kw} style={{ background: "var(--accent-wash, #f0faf0)", border: "1px solid var(--accent, #4caf50)", borderRadius: "20px",
                      padding: "3px 10px", fontSize: "12px", color: "var(--ink, #333)", display: "inline-flex", alignItems: "center", gap: "4px" }}>
                      {kw}
                      <button type="button" onClick={() => setKeywords(k => k.filter(x => x !== kw))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted, #888)", fontSize: "13px", padding: 0, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Frequency */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--muted, #888)", letterSpacing: "0.05em", display: "block", marginBottom: "5px" }}>FREQUENCY</label>
              <div style={{ display: "flex", gap: "8px" }}>
                {["daily", "weekly"].map(f => (
                  <button key={f} type="button" onClick={() => setFrequency(f)}
                    style={{ flex: 1, padding: "8px", borderRadius: "8px", border: "2px solid",
                      borderColor: frequency === f ? "var(--accent, #4caf50)" : "var(--border, #ddd)",
                      background: frequency === f ? "var(--accent-wash, #f0faf0)" : "var(--card, #fff)",
                      color: "var(--ink, #333)", fontWeight: frequency === f ? "600" : "400",
                      cursor: "pointer", fontSize: "13px" }}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Email */}
            <div>
              <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--muted, #888)", letterSpacing: "0.05em", display: "block", marginBottom: "5px" }}>SEND RESULTS TO</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com" style={inp} />
            </div>

            {feedback && (
              <p style={{ color: feedback.type === "success" ? "var(--accent, #4caf50)" : "#e53935",
                fontSize: "13px", margin: "0" }}>{feedback.msg}</p>
            )}

            <button type="button" onClick={handleSave} disabled={saving}
              className="upgrade-modal-cta" style={{ marginTop: "2px" }}>
              {saving ? "Saving…" : "Save Schedule"}
            </button>

            {/* Saved schedules list */}
            {schedules !== null && (
              <div style={{ textAlign: "left", borderTop: "1px solid var(--border, #f0f0f0)", paddingTop: "10px" }}>
                <p style={{ fontSize: "11px", color: "var(--muted, #aaa)", margin: "0 0 6px", fontWeight: "700", letterSpacing: "0.05em" }}>
                  YOUR SCHEDULES {schedules.length === 0 ? "— none yet" : ""}
                </p>
                {schedules.map(s => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: "8px",
                    padding: "8px 0", borderBottom: "1px solid var(--border, #f8f8f8)" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "12px", color: "var(--ink, #333)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.url}
                      </div>
                      <div style={{ fontSize: "11px", color: "var(--muted, #aaa)" }}>
                        {s.frequency} · {s.keywords?.join(", ")}
                      </div>
                    </div>
                    <div onClick={() => handleToggle(s.id)}
                      title={s.active ? "Pause" : "Resume"}
                      style={{ width: "36px", height: "20px", borderRadius: "10px", cursor: "pointer", flexShrink: 0,
                        background: s.active ? "var(--accent, #4caf50)" : "var(--border, #ccc)", position: "relative", transition: "background 0.2s" }}>
                      <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "#fff",
                        position: "absolute", top: "2px", transition: "left 0.2s",
                        left: s.active ? "18px" : "2px", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
                    </div>
                    <button onClick={() => handleDelete(s.id)}
                      style={{ background: "none", border: "none", color: "#e53935", cursor: "pointer",
                        fontSize: "14px", padding: "0 2px", flexShrink: 0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Results area pieces (2026-10 results redesign) ─────────────────────────
// Stroke icons drawn in currentColor, so they follow each theme's text/accent.
const ResultIcon = ({ name }) => {
  const paths = {
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
    browser: <><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M3 9h18M7 6.5h.01M10 6.5h.01" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    alert: <><path d="M12 4 2.5 20h19z" /><path d="M12 10v4M12 17h.01" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    download: <path d="M12 4v11m0 0-4-4m4 4 4-4M5 19h14" />,
    link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></>,
    compare: <path d="M7 7h12m0 0-3-3m3 3-3 3M17 17H5m0 0 3-3m-3 3 3 3" />,
    file: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></>,
    notfound: <><circle cx="11" cy="11" r="6" /><path d="m20 20-4.2-4.2M8.5 11h5" /></>,
    chevron: <path d="M6 9l6 6 6-6" />,
    hand: <><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></>,
    door: <><path d="M5 20V5a1 1 0 0 1 1-1h9v16M3 20h18M19 20V9" /><path d="M11.5 12h.01" /></>,
    tool: <><path d="M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3z" /><path d="M6 4l3 3-2 2-3-3" /></>,
    wifi: <path d="M4 9.5a12 12 0 0 1 16 0M7 13a7.5 7.5 0 0 1 10 0M10 16.5a3 3 0 0 1 4 0M12 20h.01" />,
    lock: <><rect x="5" y="10.5" width="14" height="9.5" rx="2" /><path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" /></>,
    check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
    note: <><circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5h.01" /></>,
  };
  return (
    <svg className={`r-icon r-icon-${name}`} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[name]}
    </svg>
  );
};

// A notice above the results. "info" uses the accent; "warn" (partial or
// skipped results) uses a muted ochre. Replaces the pink/cyan gradient
// banner, which was a light block on the dark page.
// Floating reminder for a signed-in customer whose renewal failed (Dodo "on_hold").
// The email with the payment link isn't always seen, so the site says it too.
// "Later" hides it for this browser session only.
const RENEWAL_REMINDER_HIDDEN_KEY = "wowowl_renewal_reminder_hidden";
function RenewalReminder({ user, onPay }) {
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(RENEWAL_REMINDER_HIDDEN_KEY) === "1"; } catch { return false; }
  });
  const [busy, setBusy] = useState(false);
  if (!user || user.subscription_status !== "on_hold" || hidden) return null;
  const hide = () => {
    try { sessionStorage.setItem(RENEWAL_REMINDER_HIDDEN_KEY, "1"); } catch { /* storage blocked: hide for this page only */ }
    setHidden(true);
  };
  const pay = async () => {
    setBusy(true);
    try { await onPay(); } finally { setBusy(false); }
  };
  return (
    <div className="renewal-reminder" role="status">
      <div className="renewal-reminder-title">Your Pro renewal didn't go through</div>
      <p className="renewal-reminder-text">
        {user.plan === "pro"
          ? "Pay the renewal to keep Pro without a break."
          : "Pro is paused until the payment goes through. Paying restores it right away."}
      </p>
      <div className="renewal-reminder-actions">
        <button type="button" className="renewal-reminder-pay" disabled={busy} onClick={pay}>
          {busy ? "Opening…" : "Pay renewal"}
        </button>
        <button type="button" className="renewal-reminder-later" onClick={hide}>Later</button>
      </div>
    </div>
  );
}

function ResultNotice({ kind = "info", icon = "info", children }) {
  return (
    <div className={`r-notice ${kind === "warn" ? "r-notice-warn" : ""}`}>
      <ResultIcon name={icon} />
      <div>{children}</div>
    </div>
  );
}

// A scan that couldn't run or couldn't read the site, shown where results
// would appear. "warn" is the default; "info" for neutral outcomes
// (cancelled, an expired share link).
function ScanProblemCard({ icon, tone = "warn", title, children, actions, fine }) {
  return (
    <div className={`scan-problem${tone === "info" ? " scan-problem-info" : ""}`} role="alert">
      <span className="scan-problem-icon"><ResultIcon name={icon} /></span>
      <div className="scan-problem-body">
        <h3 className="scan-problem-title">{title}</h3>
        {children && <p className="scan-problem-text">{children}</p>}
        {actions && <div className="scan-problem-actions">{actions}</div>}
        {fine && <div className="scan-problem-fine">{fine}</div>}
      </div>
    </div>
  );
}

// A problem with what was typed, shown right under that field.
function FieldProblem({ children }) {
  return (
    <div className="field-problem" role="alert">
      <ResultIcon name="note" />
      <span>{children}</span>
    </div>
  );
}

// Host shown muted, path emphasised — the path is what tells pages apart.
function splitPageUrl(url) {
  try {
    const u = new URL(url);
    return { host: u.host, path: `${u.pathname}${u.search}` || "/" };
  } catch {
    return { host: "", path: url };
  }
}

function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "–";
  if (seconds < 0.001) return "Instant"; // cached results load in well under 1 ms
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return `${(seconds / 60).toFixed(1)} min`;
}

function UpgradeModal({ reason, user, countryCode, pricingTable, onUpgrade, onBuyCredits, onLogin, onClose, pageCapInfo }) {
  // A Pro subscriber hitting their monthly quota is already Pro — this needs
  // a completely different message/CTA than everyone else, who isn't Pro yet.
  const isMonthlyCapped = reason === "monthly_scans";

  const reasonText =
    reason === "daily_scans"
      ? `You've used all your free scans for today.`
      : reason === "page_cap"
      ? `${pageCapInfo?.domain || "This site"} has ${pageCapInfo?.total ?? "more"} pages — we scanned the first ${pageCapInfo?.scanned ?? "some"} (your ${user ? "Free" : "Guest"} limit). Want the rest scanned too?`
      : reason === "render_cap"
      ? `${pageCapInfo?.domain || "This site"} builds its pages with JavaScript. We opened the first ${pageCapInfo?.rendered ?? "few"} in a full browser (your ${user ? "Free" : "Guest"} limit); the rest were read without it and may be missing content. Pro and scan credits render up to 200 pages per scan.`
      : reason === "pdf_export"
      ? "PDF export with WowOwl branding is a Pro feature."
      : reason === "compare"
      ? "Before/after scan comparison is a Pro feature."
      : reason === "bulk_upload"
      ? "Bulk URL scan is a Pro feature."
      : reason === "multi_keyword"
      ? "Scanning for multiple keywords at once is a Pro feature."
      : reason === "advanced_match"
      ? "Exact match and regex search are Pro features."
      : reason === "path_filter"
      ? "Path filters (limit / exclude) are Pro features."
      : reason === "smart_search"
      ? "Smart Search (synonym & variant expansion) is a Pro feature."
      : reason === "reveal_filters"
      ? "Revealing content behind dropdown/date filters is a Pro feature."
      : reason === "max_pages"
      ? "Scanning past this page depth needs a free account — or Pro for up to 3,000 pages per scan."
      : reason === "dynamic_pages"
      ? "This site uses JavaScript rendering — scanning every page of a dynamic website needs Pro."
      : isMonthlyCapped
      ? "You've used all the scans included in your Pro plan this month — it resets at the start of next calendar month. Need more right now?"
      : "Upgrade to Pro to unlock all features.";

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={e => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>×</button>
        <div className="upgrade-modal-icon">🦉</div>
        <p className="section-eyebrow" style={{ textAlign: "center" }}>{isMonthlyCapped ? "Keep going" : "Go further"}</p>
        <h2 className="upgrade-modal-title upgrade-modal-title-editorial">
          {isMonthlyCapped ? "Monthly limit reached" : "Choose how to continue"}
        </h2>
        <p className="upgrade-modal-reason">{reasonText}</p>
        {!isMonthlyCapped && (
          <>
            {!user && (
              <div className="upgrade-signin-row">
                <div className="upgrade-signin-row-text">
                  <p className="upgrade-signin-row-title">Sign in — it's free</p>
                  <p className="upgrade-signin-row-desc">Get 2 scans a day instead of 1, plus scan history.</p>
                </div>
                <button className="upgrade-signin-row-btn" onClick={onLogin}>Sign in →</button>
              </div>
            )}
            <p className="upgrade-divider-label">Or unlock it now</p>
            <div className="upgrade-plan-rows">
              <div className="upgrade-plan-row">
                <div className="upgrade-plan-row-body">
                  <p className="upgrade-plan-row-name">Starter</p>
                  <p className="upgrade-plan-row-desc">5 scans · valid 10 days · no subscription</p>
                </div>
                <div className="upgrade-plan-row-cta">
                  <span className="upgrade-plan-row-price">{getLocalizedPrice(pricingTable, countryCode, "starter") || "₹49"}</span>
                  <button className="upgrade-plan-row-btn" onClick={() => onBuyCredits("starter")}>Buy →</button>
                </div>
              </div>
              <div className="upgrade-plan-row">
                <div className="upgrade-plan-row-body">
                  <p className="upgrade-plan-row-name">Value</p>
                  <p className="upgrade-plan-row-desc">15 scans · valid 30 days · no subscription</p>
                </div>
                <div className="upgrade-plan-row-cta">
                  <span className="upgrade-plan-row-price">{getLocalizedPrice(pricingTable, countryCode, "value") || "₹99"}</span>
                  <button className="upgrade-plan-row-btn" onClick={() => onBuyCredits("value")}>Buy →</button>
                </div>
              </div>
              <div className="upgrade-plan-row upgrade-plan-row-pro">
                <div className="upgrade-plan-row-body">
                  <p className="upgrade-plan-row-name">Pro <span className="upgrade-plan-row-pill">Popular</span></p>
                  <p className="upgrade-plan-row-desc">100 scans / month · all features · cancel anytime</p>
                </div>
                <div className="upgrade-plan-row-cta">
                  <span className="upgrade-plan-row-price">{getLocalizedPrice(pricingTable, countryCode, "pro") || "₹149"}<span>/mo</span></span>
                  <button className="upgrade-plan-row-btn" onClick={() => (user ? onUpgrade() : onLogin())}>
                    {user ? "Upgrade →" : "Sign in →"}
                  </button>
                </div>
              </div>
            </div>
            {!pricingTable[countryCode] && (
              <p style={{ fontSize: "11px", color: "var(--muted, #bbb)", textAlign: "center", margin: "4px 0 14px" }}>
                Shown in INR — your checkout price may vary by region.
              </p>
            )}
          </>
        )}
        {isMonthlyCapped && (
          <button className="upgrade-modal-cta" onClick={() => { onClose(); document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }); }}>
            Buy more scans →
          </button>
        )}
        <button className="upgrade-modal-skip" onClick={onClose}>Maybe later</button>
      </div>
    </div>
  );
}

// ── Diff helpers ──────────────────────────────────────────────────────────────
function mergeKwDiff(curKw, baseKw) {
  const all = new Set([...Object.keys(curKw || {}), ...Object.keys(baseKw || {})]);
  const out = {};
  all.forEach(kw => {
    out[kw] = { current: curKw?.[kw]?.count ?? null, baseline: baseKw?.[kw]?.count ?? null };
  });
  return out;
}

function computeDiff(current, baseline) {
  const curMap = {};
  (current.table || []).forEach(r => { curMap[r.page] = r; });
  const baseMap = {};
  (baseline.table || []).forEach(r => { baseMap[r.page] = r; });

  const allPages = new Set([...Object.keys(curMap), ...Object.keys(baseMap)]);
  const rows = [];

  allPages.forEach(page => {
    const cur = curMap[page];
    const base = baseMap[page];
    if (cur && base) {
      const delta = cur.total_count - base.total_count;
      rows.push({ page, currentCount: cur.total_count, baselineCount: base.total_count, delta,
        status: delta > 0 ? "increased" : delta < 0 ? "decreased" : "same",
        keywords: mergeKwDiff(cur.keywords, base.keywords) });
    } else if (cur) {
      rows.push({ page, currentCount: cur.total_count, baselineCount: null, delta: cur.total_count,
        status: "new", keywords: mergeKwDiff(cur.keywords, {}) });
    } else {
      rows.push({ page, currentCount: null, baselineCount: base.total_count, delta: -base.total_count,
        status: "gone", keywords: mergeKwDiff({}, base.keywords) });
    }
  });

  const order = { new: 0, increased: 1, same: 2, decreased: 3, gone: 4 };
  rows.sort((a, b) => {
    if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
    return Math.abs(b.delta) - Math.abs(a.delta);
  });
  return rows;
}

// ── ComparePicker modal ────────────────────────────────────────────────────────
function ComparePicker({ searchHistory, currentUrl, onPick, onClose, fetchingId, compareError }) {
  const normalize = url => (url || "").replace(/https?:\/\//i, "").replace(/\/$/, "").toLowerCase();
  const cUrl = normalize(currentUrl);

  const sameUrl = searchHistory.filter(h => normalize(h.url || h.urls?.[0]) === cUrl);
  const others  = searchHistory.filter(h => normalize(h.url || h.urls?.[0]) !== cUrl);

  const renderItem = (h, i) => {
    const id = h.id || h.share_id;
    const canCompare = !!h.share_id;
    return (
      <div key={id || i} style={{ display: "flex", alignItems: "center", gap: "10px",
        padding: "10px 0", borderBottom: "1px solid var(--border, #f5f5f5)",
        opacity: canCompare ? 1 : 0.45 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "13px", color: "var(--ink, #333)", fontWeight: 600,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {h.url || h.urls?.[0]}
          </div>
          <div style={{ fontSize: "11px", color: "var(--muted, #aaa)", marginTop: "2px" }}>
            {(h.words || []).join(", ")} · {parseTimestamp(h.timestamp).toLocaleString()}
            {!canCompare && <span style={{ color: "#e53935", marginLeft: "6px" }}>· no share link</span>}
          </div>
        </div>
        <button onClick={() => canCompare && onPick(h)}
          disabled={!canCompare || fetchingId === id}
          title={!canCompare ? "This scan was saved before share links were stored. Run a new scan to compare." : ""}
          style={{ padding: "5px 14px", borderRadius: "6px", border: "none",
            background: canCompare ? "var(--accent, #4caf50)" : "var(--border, #ccc)",
            color: canCompare ? "var(--on-accent, #fff)" : "#fff", fontSize: "12px",
            cursor: canCompare ? "pointer" : "not-allowed",
            fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
            opacity: fetchingId === id ? 0.6 : 1 }}>
          {fetchingId === id ? "Loading…" : "Select →"}
        </button>
      </div>
    );
  };

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal schedule-modal" style={{ maxWidth: "520px", maxHeight: "80vh", overflowY: "auto" }}
        onClick={e => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>×</button>
        <div className="upgrade-modal-icon">⇄</div>
        <h2 className="upgrade-modal-title" style={{ marginBottom: "4px" }}>Compare with a Past Scan</h2>
        <p style={{ fontSize: "12px", color: "var(--muted, #aaa)", marginBottom: "16px" }}>
          Select a past scan to diff against the current results.
        </p>

        {compareError && (
          <div style={{ background: "#ffebee", color: "#c62828", borderRadius: "8px",
            padding: "10px 14px", fontSize: "13px", marginBottom: "12px" }}>
            {compareError}
          </div>
        )}

        {searchHistory.length === 0 && (
          <p style={{ color: "var(--muted, #aaa)", fontSize: "13px", textAlign: "center", padding: "20px 0" }}>
            No past scans found. Sign in and run a few scans first.
          </p>
        )}
        {searchHistory.length > 0 && !searchHistory.some(h => h.share_id) && (
          <div style={{ background: "#fff8e1", borderRadius: "8px", padding: "10px 14px",
            fontSize: "12px", color: "#e65100", marginBottom: "12px" }}>
            ⚠️ Your past scans don't have share links yet. Run a new scan — it will be available here immediately.
          </div>
        )}

        {sameUrl.length > 0 && (
          <>
            <p style={{ fontSize: "10px", fontWeight: 700, color: "var(--accent, #4caf50)",
              letterSpacing: "0.07em", margin: "0 0 4px" }}>SAME SITE</p>
            {sameUrl.map(renderItem)}
          </>
        )}

        {others.length > 0 && (
          <>
            <p style={{ fontSize: "10px", fontWeight: 700, color: "var(--muted, #aaa)",
              letterSpacing: "0.07em", margin: "14px 0 4px" }}>OTHER SITES</p>
            {others.map(renderItem)}
          </>
        )}
      </div>
    </div>
  );
}

// ── DiffTable ──────────────────────────────────────────────────────────────────
function DiffTable({ diffRows, keywordTotals }) {
  const kwKeys = Object.keys(keywordTotals || {});
  const isMultiKw = kwKeys.length > 1;

  const rowBg = { new: "#e8f5e9", increased: "#f1f8e9", same: "transparent", decreased: "#fff8e1", gone: "#ffebee" };

  const Badge = ({ status }) => {
    const cfg = {
      new:       { label: "NEW",  bg: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)" },
      gone:      { label: "GONE", bg: "#e53935", color: "#fff" },
      increased: { label: null },
      decreased: { label: null },
      same:      { label: null },
    }[status];
    if (cfg.label) return (
      <span style={{ background: cfg.bg, color: cfg.color, borderRadius: "4px",
        padding: "1px 8px", fontSize: "11px", fontWeight: 700 }}>{cfg.label}</span>
    );
    return null;
  };

  const Delta = ({ delta, status }) => {
    if (status === "new" || status === "gone") return <Badge status={status} />;
    if (delta > 0) return <span style={{ color: "#2e7d32", fontWeight: 700 }}>+{delta} ↑</span>;
    if (delta < 0) return <span style={{ color: "#c62828", fontWeight: 700 }}>{delta} ↓</span>;
    return <span style={{ color: "#aaa", fontSize: "12px" }}>no change</span>;
  };

  const KwCell = ({ kwData }) => {
    if (!kwData) return <span style={{ color: "#ccc" }}>—</span>;
    const { current, baseline } = kwData;
    if (current === null) return <span style={{ color: "#c62828" }}>—</span>;
    if (baseline === null) return <span style={{ color: "#2e7d32", fontWeight: 600 }}>+{current}</span>;
    const d = current - baseline;
    if (d > 0) return <span style={{ color: "#2e7d32", fontWeight: 600 }}>+{d} <span style={{ color: "#aaa", fontWeight: 400, fontSize: "11px" }}>({baseline}→{current})</span></span>;
    if (d < 0) return <span style={{ color: "#c62828", fontWeight: 600 }}>{d} <span style={{ color: "#aaa", fontWeight: 400, fontSize: "11px" }}>({baseline}→{current})</span></span>;
    return <span style={{ color: "#aaa" }}>{current}</span>;
  };

  return (
    <div className="table-responsive">
      <table className="table table-bordered" style={{ fontSize: "0.88rem" }}>
        <thead>
          <tr>
            <th>Page</th>
            {isMultiKw
              ? kwKeys.map(kw => <th key={kw}>{kw}</th>)
              : <><th style={{ whiteSpace: "nowrap" }}>Before → After</th></>
            }
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          {diffRows.map((row, i) => (
            <tr key={i} style={{ background: rowBg[row.status] }}>
              <td>
                <a href={row.page} target="_blank" rel="noopener noreferrer"
                  style={{ wordBreak: "break-all" }}>{row.page}</a>
              </td>
              {isMultiKw
                ? kwKeys.map(kw => <td key={kw}><KwCell kwData={row.keywords?.[kw]} /></td>)
                : <td style={{ whiteSpace: "nowrap" }}>
                    {row.baselineCount ?? "—"} → {row.currentCount ?? "—"}
                  </td>
              }
              <td style={{ whiteSpace: "nowrap" }}>
                <Delta delta={row.delta} status={row.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  const [urls, setUrls] = useState([""]);
  const [words, setWords] = useState([""]);
  // Comma keywords the visitor chose to search as one phrase (lowercased).
  const [phraseKeywords, setPhraseKeywords] = useState(() => new Set());
  const [commaNudge, setCommaNudge] = useState(false);
  const [commaSplitNote, setCommaSplitNote] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [schemeAlt, setSchemeAlt] = useState(null); // { blockedUrl, suggestedUrl, message }
  const [taskId, setTaskId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [taskStatus, setTaskStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  // Lazy initializer runs once on mount, so this stays fixed for the whole
  // visit (reloading picks a new one; toggling dark/light mid-session does
  // not, since this state lives here rather than inside the SplashCursor
  // branch itself).
  // The cursor effect starts on the first real mouse/touch input. Until then
  // nothing is downloaded or drawn (PageSpeed never moves the mouse).
  const [splashStarted, setSplashStarted] = useState(false);
  useEffect(() => {
    const start = () => setSplashStarted(true);
    const opts = { once: true, passive: true };
    window.addEventListener("pointermove", start, opts);
    window.addEventListener("pointerdown", start, opts);
    window.addEventListener("touchstart", start, opts);
    return () => {
      window.removeEventListener("pointermove", start, opts);
      window.removeEventListener("pointerdown", start, opts);
      window.removeEventListener("touchstart", start, opts);
    };
  }, []);

  const [lightSplashColor] = useState(
    () => LIGHT_SPLASH_COLORS[Math.floor(Math.random() * LIGHT_SPLASH_COLORS.length)]
  );
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [scanTime, setScanTime] = useState(null);
  const [showNotificationPopup, setShowNotificationPopup] = useState(false);
  const [strictMatch, setStrictMatch] = useState(false);
  const [useRegex, setUseRegex] = useState(false);
  const [smartSearch, setSmartSearch] = useState(false);
  const [revealFilters, setRevealFilters] = useState(false);
  // { [seedWord]: { suggestions: [{word, selected}] } }
  const [smartExpansions, setSmartExpansions] = useState({});
  const smartFetchTimers = useRef({});
  const [pathFilter, setPathFilter] = useState("");
  const [excludePath, setExcludePath] = useState("");
  const [scopes, setScopes] = useState(["title", "headings", "meta", "body"]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHistoryPanel, setShowHistoryPanel] = useState(false); // editorial mode only — collapsible, like Advanced Options
  const [maxPages, setMaxPages] = useState(null); // null = unlimited
  const [scanProgress, setScanProgress] = useState(null); // {scanned, found, max_pages}

  const toggleScope = (scope) => {
    setScopes(prev =>
      prev.includes(scope)
        ? prev.length > 1 ? prev.filter(s => s !== scope) : prev  // keep at least one
        : [...prev, scope]
    );
  };
  const [expandedRows, setExpandedRows] = useState({});
  const [shareCopied, setShareCopied] = useState(false);
  const [multiResults, setMultiResults] = useState([]); // [{url, result, loading, error, progress}]
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [shareAllCopied, setShareAllCopied] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState(null); // {type: 'success'|'error', msg}

  const [user, setUser] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [authLoading, setAuthLoading] = useState(true);
  // Guest-only country hint for localized pricing display — signed-in users
  // get their country from /api/auth/me's own country_code field instead,
  // since that's the exact stored value checkout will actually use.
  const [guestCountryCode, setGuestCountryCode] = useState(null);
  const visitorCountryCode = user?.country_code || guestCountryCode;
  // Live from GET /api/pricing (mirrors Dodo's country-override config) —
  // {} until it loads, which getLocalizedPrice treats the same as "this
  // visitor's country isn't mapped," falling back to the plain ₹ price.
  const [pricingTable, setPricingTable] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  // Only used when upgradeReason === "page_cap" — the real per-scan numbers
  // (domain/total/scanned) for that popup's dynamic copy, since unlike every
  // other UpgradeModal reason this one needs to quote this specific scan's
  // own figures rather than a fixed, reason-keyed message.
  const [pageCapInfo, setPageCapInfo] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // Analytics tier label attached to GA4 events (see analytics.js).
  const userTier = !user ? "guest" : user.plan === "pro" ? "pro" : "free";
  // Unexpired one-timer credits (from /api/auth/me). A free user holding any
  // can use the Pro scan options — the backend spends one credit per scan
  // that uses them, and leaves their free daily scans alone.
  const creditBalance = user && user.plan !== "pro" ? (user.credit_balance || 0) : 0;
  const canUseProScan = user?.plan === "pro" || creditBalance > 0;
  // Re-reads the balance after a scan that may have spent (or refunded) a credit.
  const refreshCredits = () => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`).then(res => setUser(res.data)).catch(() => {});
  };
  // One effect instead of instrumenting each of the ~20 setShowUpgradeModal(true)
  // call sites — every upsell (Pro-gated clicks, limit hits, the page-cap popup)
  // funnels through this single state flip. Deliberately depends only on
  // showUpgradeModal so a reason change while it's already open doesn't double-count.
  useEffect(() => {
    if (showUpgradeModal) trackEvent("upgrade_modal_shown", { reason: upgradeReason || "generic", user_tier: userTier });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUpgradeModal]);

  // Mobile features slider — inert on desktop (the grid never scrolls
  // horizontally there, so the handler just never fires past index 0).
  const featuresScrollRef = useRef(null);
  const featureCardRefs = useRef([]);
  const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
  const featuresScrollFrame = useRef(null);

  const handleFeaturesScroll = () => {
    if (featuresScrollFrame.current) return;
    featuresScrollFrame.current = requestAnimationFrame(() => {
      featuresScrollFrame.current = null;
      const container = featuresScrollRef.current;
      if (!container) return;
      const containerCenter = container.getBoundingClientRect().left + container.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      featureCardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cardCenter = card.getBoundingClientRect().left + card.getBoundingClientRect().width / 2;
        const distance = Math.abs(cardCenter - containerCenter);
        if (distance < closestDistance) { closestDistance = distance; closestIndex = i; }
      });
      setActiveFeatureIndex(closestIndex);
    });
  };

  const scrollToFeature = (index) => {
    featureCardRefs.current[index]?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  };

  // Compare / diff state
  const [showComparePicker, setShowComparePicker] = useState(false);
  const [compareBaseline, setCompareBaseline] = useState(null); // { result, historyItem }
  const [compareFetchingId, setCompareFetchingId] = useState(null);
  const [compareError, setCompareError] = useState(null);
  const [diffView, setDiffView] = useState(false);

  // Bulk URL mode
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkUrlsText, setBulkUrlsText] = useState("");

  // Latest keyword list, read by the async Smart Search fetch below to drop
  // responses for words that have since been edited or removed.
  const wordsRef = useRef(words);
  wordsRef.current = words;

  // Debounced per keyword *slot*, not per word: keyed by word, typing "seo"
  // left the "se" timer running, and its late response re-added an expansion
  // for a word no longer in the form (and scanned its suggestions too).
  const fetchSmartExpansions = useCallback((word, slot = word) => {
    const w = normalizeKeyword(word);
    clearTimeout(smartFetchTimers.current[slot]);
    if (!w || w.length < 2) return;
    smartFetchTimers.current[slot] = setTimeout(async () => {
      try {
        const res = await fetch(`https://api.datamuse.com/words?ml=${encodeURIComponent(w)}&max=8`);
        const data = await res.json();
        if (!wordsRef.current.some(x => normalizeKeyword(x) === w)) return;
        // Filter out the seed word itself and pure inflections that are too similar
        const suggestions = data
          .filter(d => d.word.toLowerCase() !== w.toLowerCase())
          .slice(0, 6)
          .map(d => ({ word: d.word, selected: true }));
        if (suggestions.length === 0) return;
        setSmartExpansions(prev => ({ ...prev, [w]: { suggestions } }));
      } catch {}
    }, 600);
  }, []);

  // Counts a client-only action (PDF, CSV, share link) in the admin Features
  // tab. Fire-and-forget: a failed log must never get in the visitor's way.
  const trackFeatureUse = (feature) => {
    axios.post(`${process.env.REACT_APP_API_URL}/api/track-feature`, { feature }).catch(() => {});
  };

  const handleShare = () => {
    if (!results?.share_id) return;
    const link = `${window.location.origin}/?share=${results.share_id}`;
    trackEvent("share_link_copied", { share_type: "single" });
    trackFeatureUse("share_link");
    navigator.clipboard.writeText(link).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    });
  };

  const handleShareAll = async () => {
    const shareIds = multiResults.map(s => s.result?.share_id).filter(Boolean);
    if (shareIds.length === 0) return;
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/share/multi`, { share_ids: shareIds });
      const link = `${window.location.origin}/?multi_share=${res.data.multi_share_id}`;
      trackEvent("share_link_copied", { share_type: "multi" });
      trackFeatureUse("share_link");
      navigator.clipboard.writeText(link).then(() => {
        setShareAllCopied(true);
        setTimeout(() => setShareAllCopied(false), 2500);
      });
    } catch { /* silently fail */ }
  };

  const handleEmailResults = async () => {
    if (!emailInput.trim() || !emailInput.includes("@")) {
      setEmailFeedback({ type: "error", msg: "Enter a valid email address." });
      return;
    }
    setEmailSending(true);
    setEmailFeedback(null);

    // Only the share IDs go to the backend — it rebuilds the email (and the
    // "View full results" link) from its own stored copy of each scan, so the
    // email's content can't be anything other than a real WowOwl result.
    const shareIds = (multiResults.length > 0
      ? multiResults.map(s => s.result?.share_id)
      : [results?.share_id]
    ).filter(Boolean);
    if (shareIds.length === 0) {
      setEmailFeedback({ type: "error", msg: "These results can't be emailed. Please run the scan again." });
      setEmailSending(false);
      return;
    }

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/email-results`, {
        to_email: emailInput.trim(),
        share_ids: shareIds,
      });
      setEmailFeedback({ type: "success", msg: res.data.message });
      trackEvent("email_results", { result_count: shareIds.length });
      setEmailInput("");
      setTimeout(() => { setShowEmailForm(false); setEmailFeedback(null); }, 3000);
    } catch (err) {
      setEmailFeedback({ type: "error", msg: err.response?.data?.error || "Failed to send. Please try again." });
    } finally {
      setEmailSending(false);
    }
  };

  // Sync active multi-tab result into main display states
  useEffect(() => {
    if (multiResults.length > 0) {
      const active = multiResults[activeTabIdx];
      if (!active) return;
      setResults(active.result || null);
      setError(active.error || "");
      setScanProgress(active.progress || null);
      if (active.result?.debug_info) setScanTime(active.result.debug_info.total_time_taken || 0);
    }
  }, [multiResults, activeTabIdx]);

  // Load shared result from ?share= or ?multi_share= query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const multiShareId = params.get("multi_share");
    const shareId = params.get("share");

    if (multiShareId) {
      axios.get(`${process.env.REACT_APP_API_URL}/api/share/multi/${multiShareId}`).then(res => {
        const items = res.data.results;
        if (!items?.length) return;
        setMultiResults(items.map(r => ({ url: r.base_url, result: { ...r, is_shared: true }, loading: false, error: null, progress: null })));
        setActiveTabIdx(0);
        setUrls(items.map(r => r.base_url));
        if (items[0]?.search_words?.length) setWords(items[0].search_words);
      }).catch(() => setError({ code: "share_expired" }));
      return;
    }

    if (!shareId) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/share/${shareId}`).then(res => {
      const r = res.data.result;
      // Spread the full stored result first — this used to copy only a few
      // named fields, dropping the crawl stats (so "Pages Scanned" fell back to
      // the match count), base_url and search_words (so emailing a shared
      // result had no URL/keywords), and any newer field. Same bug class as
      // pollTaskStatus's old field whitelist.
      setResults({
        ...r,
        total_word_count: r.total_word_count || 0,
        keyword_totals: r.keyword_totals || {},
        table: r.table || [],
        is_cached: true,
        share_id: r.share_id,
        is_shared: true,
      });
      if (r.base_url) setUrls([r.base_url]);
      if (r.search_words?.length) setWords(r.search_words);
    }).catch(() => {
      setError({ code: "share_expired" });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleRow = (page) => {
    setExpandedRows(prev => ({ ...prev, [page]: !prev[page] }));
  };

  const highlightKeyword = (text, word) => {
    if (!word) return text;
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      new RegExp(`^${escaped}$`, 'i').test(part)
        ? <mark key={i} className="r-mark">{part}</mark>
        : part
    );
  };
 

const sectionIds = useMemo(
  () => ["banner", "scan", "whatiswowowl", "features", "about", "faqs", "blog", "pricing", "contact"]
    .filter(id => id !== "pricing" || user?.plan !== "pro"),
  [user?.plan]
);

// Arriving with a section hash (the static blog pages link to /#scan,
// /#pricing, /#faqs and /#contact): the browser tries to jump before React has
// rendered the section, finds nothing and stays at the top. Jump once the
// sections exist, then again as fonts, images and the signed-in plan (Pro hides
// Pricing) shift the layout, unless the visitor has already scrolled.
// "Already scrolled" is judged from real input, not scroll position: the
// browser's own scroll anchoring moves scrollY during load too.
const hashJumpUntil = useRef(0);
const jumpToHash = useCallback(() => {
  const id = decodeURIComponent(window.location.hash.slice(1));
  if (!id || Date.now() > hashJumpUntil.current) return;
  document.getElementById(id)?.scrollIntoView({ behavior: "instant", block: "start" });
}, []);
useEffect(() => {
  hashJumpUntil.current = Date.now() + 6000;
  const stop = () => { hashJumpUntil.current = 0; };
  const inputs = ["wheel", "touchstart", "keydown", "mousedown"];
  inputs.forEach(ev => window.addEventListener(ev, stop, { passive: true }));
  jumpToHash();
  document.fonts?.ready.then(jumpToHash);
  window.addEventListener("load", jumpToHash);
  const timers = [600, 2000, 4000].map(ms => setTimeout(jumpToHash, ms));
  return () => {
    inputs.forEach(ev => window.removeEventListener(ev, stop));
    window.removeEventListener("load", jumpToHash);
    timers.forEach(clearTimeout);
  };
}, [jumpToHash]);
useEffect(() => { jumpToHash(); }, [user?.plan, jumpToHash]);
const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

useEffect(() => {
  const handleScroll = () => {
    const scrollY = window.scrollY;
    const buffer = 100;

    const visibleIndex = sectionIds.findIndex(id => {
      const el = document.getElementById(id);
      if (!el) return false;
      const top = el.offsetTop;
      const bottom = top + el.offsetHeight;
      return scrollY + buffer >= top && scrollY + buffer < bottom;
    });

    if (visibleIndex !== -1 && visibleIndex !== currentSectionIndex) {
      setCurrentSectionIndex(visibleIndex);
    }
  };

  window.addEventListener("scroll", handleScroll);
  handleScroll(); // run on load
  return () => window.removeEventListener("scroll", handleScroll);
}, [sectionIds, currentSectionIndex]);



const scrollToSection = (direction) => {
  const nextIndex = direction === "down"
    ? currentSectionIndex + 1
    : currentSectionIndex - 1;

  if (nextIndex >= 0 && nextIndex < sectionIds.length) {
    const target = document.getElementById(sectionIds[nextIndex]);
    if (target) {
      target.scrollIntoView({ behavior: "smooth" });
    }
  }
};

  // Pagination for Search History
  const [historyPage, setHistoryPage] = useState(1);
  const historyRowsPerPage = 10;
  const totalHistoryPages = Math.ceil(
    searchHistory.length / historyRowsPerPage
  );
  // Deleting the last item on the last page would otherwise leave an empty page.
  useEffect(() => {
    const last = Math.max(1, totalHistoryPages);
    if (historyPage > last) setHistoryPage(last);
  }, [historyPage, totalHistoryPages]);

  // iOS Safari (and Chrome for iOS, which Apple requires to use the same
  // WebKit engine) never defines the Notification API at all — not "denied
  // by default", the global itself doesn't exist. Referencing it directly
  // throws ReferenceError: Can't find variable: Notification. `typeof` is
  // the one safe way to check for a possibly-undefined global without
  // throwing. This was called unconditionally from a mount-time useEffect
  // below, so on affected devices it crashed immediately on every page
  // load — confirmed live via a real iPhone, not just reasoned about — and
  // with no error boundary around the app's main content, React unmounted
  // the entire tree, leaving only the static background visible.
  const supportsNotifications = typeof Notification !== "undefined";

  // Remembers that the visitor answered the "Enable notifications" popup (Skip
  // or Allow), so it isn't shown on every scan. Wrapped in try/catch because
  // localStorage throws when site data is blocked.
  const NOTIF_PROMPT_KEY = "wowowl_notif_prompt_dismissed";
  const notifPromptDismissed = () => {
    try { return localStorage.getItem(NOTIF_PROMPT_KEY) === "1"; } catch { return false; }
  };
  const dismissNotifPrompt = () => {
    try { localStorage.setItem(NOTIF_PROMPT_KEY, "1"); } catch {}
    setShowNotificationPopup(false);
  };

  //dont know what for?
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const navLinks = document.querySelectorAll(".top-nav a");

    const onScroll = () => {
      const scrollPos = window.scrollY + 90;

      sections.forEach((section) => {
        if (
          scrollPos >= section.offsetTop &&
          scrollPos < section.offsetTop + section.offsetHeight
        ) {
          navLinks.forEach((link) => link.classList.remove("active"));
          const activeLink = document.querySelector(
            `.top-nav a[href="#${section.id}"]`
          );
          if (activeLink) activeLink.classList.add("active");
        }
      });
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Show slide-in notification
  const showSlideInNotification = async (title, bodyText, iconUrl = "") => {
    if (!supportsNotifications) return; // e.g. iOS Safari/Chrome-iOS — nothing to show, scan results still render in-page regardless
    // Only notify if the visitor already opted in — never prompt here, since
    // this fires on scan completion with no user gesture behind it.
    if (Notification.permission === "granted") {
      const finalIconUrl =
        iconUrl || `${process.env.REACT_APP_API_URL}/favicon.ico`;
      console.log("Icon URL used:", finalIconUrl);

      const options = {
        body: bodyText,
        icon: finalIconUrl,
        requireInteraction: false, // Notification will close automatically
      };
      try {
        const notification = new Notification(title, options);
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      } catch (err) {
        console.error("Notification failed:", err);
      }
    } else if (Notification.permission === "denied") {
      console.warn("User denied notifications. No notification shown.");
    }
  };


  useEffect(() => {
    setDarkMode(lsGet("darkMode") === "true");
    lsRemove(LEGACY_HISTORY_KEY);
    const savedHistory = lsGetJSON(GUEST_HISTORY_KEY, []);
    if (Array.isArray(savedHistory)) setSearchHistory(savedHistory);
    // No permission request on page load — browsers ignore or quiet-block
    // requests not triggered by a user action; the scan-time popup asks instead.

    const params = new URLSearchParams(window.location.search);
    const upgraded = params.get("upgraded");
    const creditsReturn = params.get("credits");
    if (upgraded || creditsReturn) {
      // Dodo's return_url landing (billing_checkout / billing_checkout_onetime).
      // Reported as a return, not a GA4 "purchase" — reaching this URL doesn't
      // by itself prove the payment succeeded; the billing webhook is the source
      // of truth for real revenue. Dodo's own status param is passed through
      // when present so the two outcomes can still be told apart in GA4.
      trackEvent("checkout_return", {
        checkout_type: upgraded ? "pro" : "credits",
        payment_status: params.get("status") || undefined,
      });
      // Our own flags plus whatever Dodo appends to the return_url.
      ["upgraded", "credits", "status", "payment_id", "subscription_id"].forEach(k => params.delete(k));
      window.history.replaceState({}, "", params.toString() ? `?${params.toString()}` : window.location.pathname);
    }
    let loginPending = false;
    try {
      loginPending = sessionStorage.getItem("wowowl_login_pending") === "1";
      sessionStorage.removeItem("wowowl_login_pending");
    } catch {}

    // Session lives in an HttpOnly cookie the browser sends automatically —
    // just ask the backend who (if anyone) it belongs to.
    axios.get(`${process.env.REACT_APP_API_URL}/api/auth/me`).then(res => {
      setUser(res.data);
      if (loginPending) trackEvent("login", { method: "Google", user_tier: res.data.plan === "pro" ? "pro" : "free" });
      if (upgraded) setShowUpgradeModal(false);
      // Signed in: show this account's history from the backend instead of
      // the device's guest history (which stays untouched for after logout).
      setSearchHistory([]);
      axios.get(`${process.env.REACT_APP_API_URL}/api/history`)
        .then(hr => setSearchHistory(hr.data)).catch(() => {});
    }).catch(() => {
      // Not signed in — best-effort IP geolocation just for pricing display.
      axios.get(`${process.env.REACT_APP_API_URL}/api/geo`)
        .then(r => setGuestCountryCode(r.data.country_code)).catch(() => {});
    }).finally(() => setAuthLoading(false));

    axios.get(`${process.env.REACT_APP_API_URL}/api/pricing`)
      .then(res => setPricingTable(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    lsSet("darkMode", darkMode);
    document.body.classList.toggle("dark-mode", darkMode);
  }, [darkMode]);

  // Always the editorial theme. Its CSS is scoped under body.theme-editorial;
  // its fonts are self-hosted and preloaded in public/index.html.
  useEffect(() => {
    document.body.classList.add("theme-editorial");
    lsRemove("wowowl_ui_variant"); // leftover preference from the classic/editorial switch
  }, []);

  // Only guest history is kept on the device. While signed in (or while the
  // auth check is still in flight) the list on screen may be the account's,
  // so it's never written here.
  useEffect(() => {
    if (user || authLoading) return;
    lsSet(GUEST_HISTORY_KEY, JSON.stringify(searchHistory.slice(0, 50)));
  }, [searchHistory, user, authLoading]);

  const handleLogin = () => {
    // Flag survives the Google OAuth round-trip (same tab), so the mount effect
    // can tell a fresh sign-in apart from an already-signed-in page load.
    trackEvent("sign_in_click", { user_tier: userTier });
    try { sessionStorage.setItem("wowowl_login_pending", "1"); } catch {}
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
  };

  const handleLogout = () => {
    axios.post(`${process.env.REACT_APP_API_URL}/api/auth/logout`).catch(() => {});
    setUser(null);
    // Drop the account's history from the screen; show the device's own guest
    // history (if any) again.
    const saved = lsGetJSON(GUEST_HISTORY_KEY, []);
    setSearchHistory(Array.isArray(saved) ? saved : []);
    setHistoryPage(1);
  };

  const handleUpgrade = async () => {
    trackEvent("begin_checkout", { checkout_type: "pro", item_name: "pro_monthly", user_tier: userTier });
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/billing/checkout`);
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      alert(err.response?.data?.error || "Could not start checkout. Please try again.");
    }
  };

  const handleBuyCredits = async (tier) => {
    if (!user) { handleLogin(); return; }
    trackEvent("begin_checkout", { checkout_type: "credits", item_name: `credits_${tier}`, user_tier: userTier });
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/billing/checkout-onetime`, { tier });
      if (res.data.checkout_url) window.location.href = res.data.checkout_url;
    } catch (err) {
      alert(err.response?.data?.error || "Could not start checkout. Please try again.");
    }
  };

  const handleManageBilling = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/billing/portal`);
      if (res.data.portal_url) window.location.href = res.data.portal_url;
    } catch (err) {
      alert(err.response?.data?.error || "Could not open the billing portal. Please try again.");
    }
  };

  // Shared GA4 payload for scan_completed across single/multi/bulk and cache hits.
  // pages_scanned comes from debug_info (every page crawled), not table.length
  // (only pages that matched).
  const scanCompletedParams = (result, extra = {}) => ({
    scan_domain: hostnameOf(result?.base_url || ""),
    pages_scanned: pagesScanned(result) ?? 0,
    pages_with_matches: pagesWithMatches(result),
    total_matches: result?.total_word_count || 0,
    has_matches: (result?.total_word_count || 0) > 0,
    cancelled: !!result?.cancelled,
    duration_sec: Math.round(result?.debug_info?.total_time_taken ?? result?.time_taken ?? 0),
    user_tier: userTier,
    ...extra,
  });

  // Bumped by every new scan and by Reset. A poll loop (or a late /api/scan
  // response) from an older scan sees a different value and stops, instead of
  // overwriting the newer scan's results or progress.
  const scanGenRef = useRef(0);

  const pollMultiUrl = async (idx, taskId, gen, retryCount = 0) => {
    const MAX_POLL_RETRIES = 3;
    if (gen !== scanGenRef.current) return;
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/task/${taskId}`);
      if (gen !== scanGenRef.current) return;
      if (response.data.state === "SUCCESS") {
        if (!response.data.result) throw new Error("SUCCESS state received with no result payload");
        trackEvent("scan_completed", scanCompletedParams(response.data.result, { scan_mode: "multi", from_cache: false }));
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, loading: false, result: response.data.result, progress: null }
          : item));
        setScanTime(response.data.result?.debug_info?.total_time_taken || 0);
        setCancelling(false);
      } else if (response.data.state === "PROGRESS") {
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, progress: response.data.progress } : item));
        setTimeout(() => pollMultiUrl(idx, taskId, gen), 1500);
      } else if (response.data.state === "PENDING") {
        setTimeout(() => pollMultiUrl(idx, taskId, gen), 2000);
      } else if (response.data.state === "REVOKED") {
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, loading: false, error: "Scan cancelled.", progress: null } : item));
        setCancelling(false);
      } else {
        // A genuine backend crash (not the user's fault) refunds whichever
        // daily/monthly scan slot this attempt spent — say so, rather than
        // leaving the user to assume they just burned their one scan on
        // nothing.
        trackEvent("scan_error", { scan_mode: "multi", error_type: "task_failed", user_tier: userTier });
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, loading: false, error: { code: "scan_failed", quotaRefunded: !!response.data.quota_refunded } }
          : item));
        setCancelling(false);
      }
    } catch {
      // Same transient-failure tolerance as pollTaskStatus: one dropped poll
      // shouldn't discard a scan that may already have finished.
      if (retryCount < MAX_POLL_RETRIES) {
        setTimeout(() => pollMultiUrl(idx, taskId, gen, retryCount + 1), 2000);
        return;
      }
      trackEvent("scan_error", { scan_mode: "multi", error_type: "poll_failed", user_tier: userTier });
      setMultiResults(prev => prev.map((item, i) => i === idx
        ? { ...item, loading: false, error: { code: "poll_lost" } } : item));
      setCancelling(false);
    }
  };

  // Guest-only proof-of-work: finds a nonce such that SHA-256(challenge + nonce)
  // starts with `difficulty` leading zeros. Yields periodically so the loop
  // never freezes the tab on slower devices.
  const solvePowChallenge = async (challenge, difficulty) => {
    const prefix = "0".repeat(difficulty);
    const encoder = new TextEncoder();
    let nonce = 0;
    for (;;) {
      const buf = await crypto.subtle.digest("SHA-256", encoder.encode(challenge + nonce));
      const hex = [...new Uint8Array(buf)].map(b => b.toString(16).padStart(2, "0")).join("");
      if (hex.startsWith(prefix)) return nonce;
      nonce++;
      if (nonce % 500 === 0) await new Promise(r => setTimeout(r, 0));
    }
  };

  // Signed-in/Pro users skip this entirely — the backend only requires a
  // solved challenge when the caller is a guest (see /api/scan's pow gate).
  const getPowFields = async () => {
    if (user) return {};
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/scan/challenge`);
      const { token, challenge, difficulty } = res.data;
      const nonce = await solvePowChallenge(challenge, difficulty);
      return { pow_token: token, pow_nonce: nonce };
    } catch {
      return {}; // best-effort — if issuance fails, let the real /api/scan call surface the actual error
    }
  };

  // Shared by both scan call sites: attaches a solved PoW challenge for guests,
  // and silently retries once if the backend rejects it (expired/already-used
  // token racing a slow solve) rather than surfacing a scary error immediately.
  const postScan = async (payload) => {
    const powFields = await getPowFields();
    let response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/scan`,
      { ...payload, ...powFields },
      { headers: { "Content-Type": "application/json" }, validateStatus: s => s >= 200 && s < 500 }
    );
    if (!user && (response.data?.error === "pow_required" || response.data?.error === "pow_invalid")) {
      const retryFields = await getPowFields();
      response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/scan`,
        { ...payload, ...retryFields },
        { headers: { "Content-Type": "application/json" }, validateStatus: s => s >= 200 && s < 500 }
      );
    }
    return response;
  };

  // compareGroup: one id shared by every site of a Compare run, so the admin
  // Features tab can count runs rather than individual site scans.
  const startMultiScan = async (targetUrl, idx, activeWords, forceRefresh, gen, compareGroup) => {
    try {
      const response = await postScan({
        url: targetUrl, words: activeWords, force_refresh: forceRefresh,
        strict_match: strictMatch, use_regex: useRegex, smart_search: smartSearch, reveal_filters: revealFilters, path_filter: pathFilter.trim() || null, exclude_path: excludePath.trim() || null,
        scopes: scopes.length === 4 ? null : scopes, max_pages: maxPages || null,
        compare_group: compareGroup,
      });
      if (response.data?.used_credit || creditBalance > 0) refreshCredits();
      if (gen !== scanGenRef.current) return;
      if (response.status === 403 && response.data.error === "limit_reached") {
        trackEvent("limit_reached", { limit_type: response.data.limit_type, scan_mode: "multi", user_tier: userTier });
        setUpgradeReason(response.data.limit_type);
        setShowUpgradeModal(true);
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, loading: false, error: "Daily scan limit reached." } : item));
      } else if (response.data.is_cached) {
        trackEvent("scan_completed", scanCompletedParams(response.data.result, { scan_mode: "multi", from_cache: true }));
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, loading: false, result: { ...response.data.result, is_cached: true } } : item));
      } else if (response.data.task_id) {
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, taskId: response.data.task_id } : item));
        pollMultiUrl(idx, response.data.task_id, gen);
      } else {
        setMultiResults(prev => prev.map((item, i) => i === idx
          ? { ...item, loading: false, error: problemFromResponse(response.status, response.data, targetUrl) }
          : item));
      }
    } catch (err) {
      if (gen !== scanGenRef.current) return;
      const problem = err?.response
        ? problemFromResponse(err.response.status, err.response.data, targetUrl)
        : { code: "network" };
      setMultiResults(prev => prev.map((item, i) => i === idx
        ? { ...item, loading: false, error: problem } : item));
    }
  };

  const pollTaskStatus = async (taskId, gen, retryCount = 0) => {
    const MAX_POLL_RETRIES = 3;
    if (gen !== scanGenRef.current) return;
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/task/${taskId}`;
      const response = await axios.get(apiUrl);
      if (gen !== scanGenRef.current) return;

      console.log("Full API Response:", response.data); // Debug response

      if (response.data.state === "SUCCESS") {
        if (!response.data.result) {
          // A SUCCESS state should always carry a real result object (even an
          // empty/no-match scan still returns one) - if it's missing, this
          // response was corrupted or cut short in transit, not a genuine
          // backend outcome. Treat it like any other transient poll failure
          // (retry) rather than crashing or rendering a fake empty result.
          throw new Error("SUCCESS state received with no result payload");
        }
        const { total_word_count, keyword_totals, table, debug_info, share_id, base_url, search_words, cancelled } = response.data.result;
        trackEvent("scan_completed", scanCompletedParams(response.data.result, {
          scan_mode: response.data.result.bulk_mode ? "bulk" : "single", from_cache: false,
        }));

        setResults({
          ...response.data.result,
          total_word_count: total_word_count || 0,
          keyword_totals: keyword_totals || {},
          table: table || [],
          is_cached: false,
          share_id: share_id || null,
        });
        setScanTime(debug_info?.total_time_taken || 0);
        setTaskStatus("SUCCESS");
        setLoading(false);
        setCancelling(false);

        // Cross-sell: the scan hit its guest/free page cap on a site with real pages
        // left unscanned (page_cap_hit is only ever true when the backend has an
        // honest sitemap-derived total to quote — see scan_task). Auto-shown, unlike
        // every other UpgradeModal trigger (which are all user-initiated clicks) —
        // deliberately more assertive since this is the one moment a free/guest user
        // just got real value and is most receptive to "there's more here."
        if (response.data.result.render_cap_hit) {
          let domain = base_url;
          try { domain = new URL(base_url).hostname; } catch {}
          setPageCapInfo({ domain, rendered: response.data.result.rendered_pages });
          setUpgradeReason("render_cap");
          setShowUpgradeModal(true);
        } else if (response.data.result.page_cap_hit) {
          let domain = base_url;
          try { domain = new URL(base_url).hostname; } catch {}
          setPageCapInfo({
            domain,
            total: response.data.result.total_site_pages,
            scanned: response.data.result.scanned_pages?.length ?? null,
          });
          setUpgradeReason("page_cap");
          setShowUpgradeModal(true);
        }

        // Save history with share_id now that scan is complete, then re-fetch
        if (user && share_id) {
          axios.post(`${process.env.REACT_APP_API_URL}/api/history`, {
            url: base_url, words: search_words, share_id,
          })
            .then(() => axios.get(`${process.env.REACT_APP_API_URL}/api/history`))
            .then(hr => setSearchHistory(hr.data))
            .catch(() => {});
        }

        showSlideInNotification(
          cancelled ? "Scan Cancelled" : "Scan Complete!",
          cancelled ? "The scan was cancelled — showing partial results, if any." : `The scan completed successfully! Total Word Count: ${total_word_count}`,
          Wowowl
        );
      } else if (response.data.state === "PROGRESS") {
        setScanProgress(response.data.progress);
        setTimeout(() => pollTaskStatus(taskId, gen), 1500);
      } else if (response.data.state === "PENDING") {
        setTimeout(() => pollTaskStatus(taskId, gen), 2000);
      } else if (response.data.state === "REVOKED") {
        setError("Scan cancelled.");
        setLoading(false);
        setCancelling(false);
      } else {
        // A genuine backend crash (not the user's fault) refunds whichever
        // daily/monthly scan slot this attempt spent — say so, rather than
        // leaving the user to assume they just burned their one scan on
        // nothing.
        trackEvent("scan_error", { scan_mode: "single", error_type: "task_failed", user_tier: userTier });
        setError({ code: "scan_failed", quotaRefunded: !!response.data.quota_refunded });
        setLoading(false);
        setCancelling(false);
      }
    } catch (err) {
      // A single transient hiccup (a brief network blip, a momentarily
      // unresponsive tab, a corrupted/truncated response) shouldn't throw
      // away a scan that may have already finished successfully on the
      // backend - retry a few times with a short backoff before giving up.
      // Naturally resets to 0 on the next poll cycle, since the
      // PROGRESS/PENDING branches above always recurse without passing a
      // retryCount.
      if (gen !== scanGenRef.current) return;
      if (retryCount < MAX_POLL_RETRIES) {
        console.warn(`Poll attempt ${retryCount + 1} failed, retrying:`, err);
        setTimeout(() => pollTaskStatus(taskId, gen, retryCount + 1), 2000);
        return;
      }
      console.error("Error polling task status (giving up after retries):", err);
      trackEvent("scan_error", { scan_mode: "single", error_type: "poll_failed", user_tier: userTier });
      setError({ code: "poll_lost", taskId });
      setLoading(false);
      setCancelling(false);
    }
  };

  // Cancels whatever scan(s) are currently in flight — the single-URL taskId
  // and/or any still-loading multi-URL-compare tabs. Fires the cancel request
  // for each and lets the existing poll loops (pollTaskStatus/pollMultiUrl)
  // discover the result — cancellation isn't always instant (a page fetch
  // already in flight runs to completion first), so this only flips a
  // "Cancelling…" flag rather than tearing down the loading state itself.
  const handleCancelScan = async () => {
    const idsToCancel = [
      taskId,
      ...multiResults.filter(s => s.loading && s.taskId).map(s => s.taskId),
    ].filter(Boolean);
    if (idsToCancel.length === 0) return;
    setCancelling(true);
    await Promise.all(idsToCancel.map(id =>
      axios.post(`${process.env.REACT_APP_API_URL}/api/task/${id}/cancel`).catch(() => {})
    ));
  };

  const handleSubmit = async (e, forceRefresh = false, overrideUrl = null) => {
    e.preventDefault();
    // Don't guess what "one, two" means: the visitor picks under the field first.
    if (commaChoiceIndex !== -1) {
      setCommaNudge(true);
      return;
    }
    const gen = ++scanGenRef.current;

    setLoading(true);
    setError("");
    setSchemeAlt(null);
    setResults(null);
    setTaskId(null);
    setTaskStatus(null);
    setExpandedRows({});
    setScanProgress(null);
    setMultiResults([]);
    setShowNotFound(false);
    setCancelling(false);

    // Smart Search: seeds plus selected related words, capped at the backend's
    // 10-keyword limit. Uncapped, two seeds with six suggestions each sent 14
    // and every such scan was rejected.
    const seedWords = words.map(normalizeKeyword).filter(Boolean);
    const activeWords = smartSearch ? smartScanWords : seedWords;
    const activeUrls = overrideUrl ? [overrideUrl] : urls.map(u => u.trim()).filter(Boolean);
    if (overrideUrl) setUrls([overrideUrl]);

    // Bulk mode: parse textarea URLs
    const parsedBulkUrls = bulkMode
      ? bulkUrlsText.split("\n").map(u => u.trim()).filter(Boolean)
      : [];

    // Checked here so a pasted sentence or a single word gets a plain
    // explanation under the field instead of the server's "can't find this
    // site" — and nothing is sent.
    const addresses = bulkMode ? parsedBulkUrls : activeUrls;
    const notAddress = addresses.find(u => !looksLikeWebAddress(u));
    const formProblem = !addresses.length ? { code: "missing_url" }
      : notAddress ? { code: "not_a_url", url: notAddress }
      : activeWords.length === 0 ? { code: "missing_keyword" }
      : null;
    if (formProblem) {
      setError(formProblem);
      setLoading(false);
      return;
    }

    // Optional, non-blocking offer to enable completion notifications — the
    // scan below starts regardless. This used to `return` here whenever
    // permission was still "default", and "Skip" only closed the popup
    // without changing that, so anyone who skipped (or dismissed/ignored the
    // browser's own prompt) could never start a scan at all. Now it's offered
    // at most until the visitor answers it once. supportsNotifications guards
    // iOS Safari/Chrome-iOS, where the Notification global doesn't exist.
    if (supportsNotifications && Notification.permission === "default" && !notifPromptDismissed()) {
      setShowNotificationPopup(true);
    }

    const scanMode = bulkMode ? "bulk" : activeUrls.length > 1 ? "multi" : "single";
    trackEvent("scan_started", {
      scan_mode: scanMode,
      scan_domain: hostnameOf(bulkMode ? parsedBulkUrls[0] : activeUrls[0]),
      url_count: bulkMode ? parsedBulkUrls.length : activeUrls.length,
      keyword_count: activeWords.length,
      exact_match: !!strictMatch,
      regex: !!useRegex,
      smart_search: !!smartSearch,
      reveal_filters: !!revealFilters,
      path_filtered: !!(pathFilter.trim() || excludePath.trim()),
      force_refresh: !!forceRefresh,
      user_tier: userTier,
    });

    // Multi-URL: fire all scans in parallel (not applicable in bulk mode)
    if (!bulkMode && activeUrls.length > 1) {
      setLoading(false);
      const initial = activeUrls.map(u => ({ url: u, result: null, loading: true, error: null, progress: null, taskId: null }));
      setMultiResults(initial);
      setActiveTabIdx(0);
      const newHistoryItem = { urls: activeUrls, url: activeUrls[0], words: activeWords, timestamp: new Date().toISOString() };
      setSearchHistory(prev => [newHistoryItem, ...prev]);
      const compareGroup = `cmp_${Math.random().toString(36).slice(2, 10)}`;
      activeUrls.forEach((u, idx) => startMultiScan(u, idx, activeWords, forceRefresh, gen, compareGroup));
      return;
    }

    // Single URL (or bulk mode)
    try {
      const response = await postScan({
        url: bulkMode ? parsedBulkUrls[0] : activeUrls[0],
        words: activeWords,
        force_refresh: forceRefresh,
        strict_match: strictMatch,
        use_regex: useRegex,
        smart_search: smartSearch,
        reveal_filters: revealFilters,
        path_filter: pathFilter.trim() || null,
        exclude_path: excludePath.trim() || null,
        scopes: scopes.length === 4 ? null : scopes,
        max_pages: maxPages || null,
        ...(bulkMode && { specific_urls: parsedBulkUrls }),
      });

      const data = response.data;
      // Keep the credit count shown in the form accurate — this scan may have
      // spent one, or had one refunded (e.g. a typo'd domain).
      if (data.used_credit || creditBalance > 0) refreshCredits();
      // Reset (or another scan) happened while this request was in flight.
      if (gen !== scanGenRef.current) return;

      if (response.status === 403 && data.error === "limit_reached") {
        trackEvent("limit_reached", { limit_type: data.limit_type, scan_mode: scanMode, user_tier: userTier });
        setUpgradeReason(data.limit_type);
        setShowUpgradeModal(true);
        setLoading(false);
        return;
      }

      if (response.status === 403 && data.error === "robots_alt_available") {
        trackEvent("scan_error", { scan_mode: scanMode, error_type: "robots_alt_available", user_tier: userTier });
        setSchemeAlt({ blockedUrl: data.blocked_url, suggestedUrl: data.suggested_url, message: data.message });
        setLoading(false);
        return;
      }

      const submittedUrl = bulkMode ? parsedBulkUrls[0] : activeUrls[0];
      if (response.status === 403 && typeof data.error === "string" && data.error.includes("robots.txt")) {
        trackEvent("scan_error", { scan_mode: scanMode, error_type: "robots_blocked", user_tier: userTier });
        setError(problemFromResponse(response.status, data, submittedUrl));
        setLoading(false);
        return;
      }

      // Any other 4xx with a plain error string (e.g. "A URL is required.",
      // or bulk mode's same-domain validation message) — show it directly
      // instead of falling through to a generic "Unexpected API response."
      if (response.status >= 400 && typeof data.error === "string") {
        // error_type is the backend's own fixed message (e.g. "Could not resolve
        // hostname."), trimmed to GA4's 100-char param limit — never user input.
        trackEvent("scan_error", { scan_mode: scanMode, error_type: data.error.slice(0, 100), user_tier: userTier });
        setError(problemFromResponse(response.status, data, submittedUrl));
        setLoading(false);
        return;
      }

      if (data.is_cached) {
        trackEvent("scan_completed", scanCompletedParams(data.result, { scan_mode: scanMode, from_cache: true }));
        setResults({ ...data.result, is_cached: true, cache_fetch_time: data.result.cache_fetch_time || 0 });
        setLoading(false);
        // Save history with share_id for cached results too
        const sid = data.result?.share_id;
        if (user && sid) {
          axios.post(`${process.env.REACT_APP_API_URL}/api/history`, {
            url: bulkMode ? parsedBulkUrls[0] : activeUrls[0], words: activeWords, share_id: sid,
          })
            .then(() => axios.get(`${process.env.REACT_APP_API_URL}/api/history`))
            .then(hr => setSearchHistory(hr.data))
            .catch(() => {});
        }
      } else if (data.task_id) {
        const scanUrl = bulkMode ? parsedBulkUrls[0] : activeUrls[0];
        const newHistoryItem = { url: scanUrl, words: activeWords, timestamp: new Date().toISOString() };
        setSearchHistory((prev) => [newHistoryItem, ...prev]);
        setTaskId(data.task_id);
        pollTaskStatus(data.task_id, gen);
      } else {
        throw new Error("Unexpected API response.");
      }
    } catch (err) {
      if (gen !== scanGenRef.current) return;
      console.error("Error during API request:", err);
      trackEvent("scan_error", { scan_mode: scanMode, error_type: "request_failed", user_tier: userTier });
      setError(err.response
        ? problemFromResponse(err.response.status, err.response.data, bulkMode ? parsedBulkUrls[0] : activeUrls[0])
        : { code: "network" });
      setLoading(false);
    }
  };

  const handlePickBaseline = async (historyItem) => {
    if (!historyItem.share_id) {
      setCompareError("This scan doesn't have a saved share link and can't be used for comparison.");
      return;
    }
    const id = historyItem.id || historyItem.share_id;
    setCompareFetchingId(id);
    setCompareError(null);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/share/${historyItem.share_id}`);
      setCompareBaseline({ result: res.data.result, historyItem });
      setShowComparePicker(false);
      setDiffView(true);
    } catch (err) {
      setCompareError(
        err.response?.status === 404
          ? "This scan has expired (> 7 days old) and can no longer be compared."
          : "Failed to load the past scan. Please try another."
      );
    } finally {
      setCompareFetchingId(null);
    }
  };

  const handleReset = (e) => {
    // Reset is the same type="submit" button as Scan (its label/handler swap
    // once results exist) — without this, clicking it also submitted the form
    // and immediately started a new scan, spending a quota slot.
    e?.preventDefault?.();
    scanGenRef.current += 1;
    setUrls([""]);
    setWords([""]);
    setResults(null);
    setError("");
    setTaskId(null);
    setTaskStatus(null);
    setCurrentPage(1);
    setLoading(false);
    setMultiResults([]);
    setActiveTabIdx(0);
    setCompareBaseline(null);
    setDiffView(false);
    setShowComparePicker(false);
    setBulkMode(false);
    setBulkUrlsText("");
    setExcludePath("");
    setUseRegex(false);
    setSmartSearch(false);
    setSmartExpansions({});
    setShowNotFound(false);
    setCancelling(false);
  };

  // Re-runs the scan with whatever the form holds now. Tip buttons in the
  // no-matches panel change a setting and then ask for this, so the scan
  // runs on the next render, once the new setting is in state.
  const [rescanRequested, setRescanRequested] = useState(false);
  useEffect(() => {
    if (!rescanRequested) return;
    setRescanRequested(false);
    handleSubmit({ preventDefault() {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescanRequested]);
  const resubmit = () => handleSubmit({ preventDefault() {} });

  // Leaves the result/problem view and puts the cursor in a field, so the
  // visitor can edit and press Scan (the button reads "Reset" while results
  // are showing).
  const backToForm = (field) => {
    scanGenRef.current += 1;
    setResults(null);
    setError("");
    setSchemeAlt(null);
    setShowNotFound(false);
    setLoading(false);
    setMultiResults([]);
    setTimeout(() => {
      const el = field === "keyword"
        ? document.querySelector(".keyword-chip-input")
        : document.getElementById(bulkMode ? "scan-bulk-input" : "scan-url-input");
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus({ preventScroll: true });
      if (el.select) el.select();
    }, 50);
  };

  // A lost poll: the scan may have finished, so ask again for the same task
  // instead of starting (and paying for) a new one.
  const checkScanAgain = (tid) => {
    setError("");
    setLoading(true);
    pollTaskStatus(tid, scanGenRef.current);
  };

  const handleDeleteHistory = () => {
    if (window.confirm("Are you sure you want to clear all history?")) {
      setSearchHistory([]);
      setHistoryPage(1);
      if (!user) lsRemove(GUEST_HISTORY_KEY);
      // For logged-in users the list is actually sourced from the backend on
      // every reload — clearing local state alone left it reappearing after
      // a refresh, since the DB rows were never touched.
      if (user) {
        axios.delete(`${process.env.REACT_APP_API_URL}/api/history`).catch(() => {});
      }
    }
  };

  const handleDeleteSearch = (index) => {
    const item = searchHistory[index];
    setSearchHistory((prev) => prev.filter((_, i) => i !== index));
    // Same reasoning as handleDeleteHistory above — only backend-sourced
    // items have an `id`; anonymous/local-only entries never did and are
    // already fully handled by the searchHistory->localStorage sync effect.
    if (item?.id && user) {
      axios.delete(`${process.env.REACT_APP_API_URL}/api/history/${item.id}`).catch(() => {});
    }
  };

  const handleSearchAgain = (search) => {
    setUrls(search.urls || [search.url || ""]);
    setWords(search.words || [search.word || ""]);
    setResults(null);
    setError("");
    setTaskId(null);
    setTaskStatus(null);
    setCurrentPage(1);
    setMultiResults([]);
    setActiveTabIdx(0);
  };

  // Pagination Handling for Search History
  const handleHistoryPageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalHistoryPages) {
      setHistoryPage(newPage);
    }
  };

  const paginatedRows =
    results?.table.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    ) || [];
  const totalPages = results?.table
    ? Math.ceil(results.table.length / rowsPerPage)
    : 0;

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  // Shared between classic's always-visible history section and editorial's
  // collapsible one inside the scan card — same grid/pagination markup,
  // just placed differently per variant.
  const problem = toProblem(error);
  const placement = problemPlacement(problem);

  const clearFieldProblem = (field) => {
    if (placement === field) setError("");
  };

  const applySuggestion = (badUrl, fixedUrl) => {
    const badHost = hostOf(badUrl);
    if (bulkMode) {
      setBulkUrlsText(text => text.split("\n").map(l => (hostOf(l.trim()) === badHost ? fixedUrl : l)).join("\n"));
    } else {
      setUrls(prev => {
        const i = Math.max(0, prev.findIndex(u => hostOf(u.trim()) === badHost));
        return prev.map((u, j) => (j === i ? fixedUrl : u));
      });
    }
    setError("");
  };

  const fieldProblemMessage = (p) => {
    const host = hostOf(p.url);
    switch (p.code) {
      case "not_a_url":
      case "invalid_url":
        return (
          <><b>That doesn't look like a web address.</b> Enter just the site, like <code>example.com</code> or{" "}
            <code>https://example.com/blog</code>.</>
        );
      case "dns_not_found": {
        const fix = suggestAddress(p.url);
        return (
          <><b>We can't find {host || "that site"}.</b> Check the spelling of the address.
            {fix && <> Did you mean{" "}
              <button type="button" className="field-problem-fix" onClick={() => applySuggestion(p.url, fix.url)}>
                {fix.label}
              </button>?</>}
          </>
        );
      }
      case "dns_timeout":
        return (
          <><b>{host || "The site"} didn't answer in time.</b> This is usually temporary.{" "}
            <button type="button" className="field-problem-fix" onClick={resubmit}>Try again</button></>
        );
      case "private_address":
        return (
          <><b>WowOwl can only scan public websites.</b> This address points to a private network, like your own
            computer or office.</>
        );
      case "missing_url":
        return bulkMode ? "Paste at least one web address, one per line." : "Enter a website to scan.";
      case "bulk_other_domain": {
        const others = p.otherUrls || [];
        const domain = hostOf(p.domain) || "one site";
        return others.length ? (
          <><b>A bulk scan covers one website.</b> These aren't on {domain}:{" "}
            {others.slice(0, 5).map(u => <code key={u} className="field-problem-chip">{u}</code>)}
            {others.length > 5 && ` and ${others.length - 5} more`}.{" "}
            <button type="button" className="field-problem-fix" onClick={() => {
              const drop = new Set(others.map(u => u.trim()));
              setBulkUrlsText(text => text.split("\n").filter(l => !drop.has(l.trim())).join("\n"));
              setError("");
            }}>Remove them</button></>
        ) : (
          <><b>A bulk scan covers one website.</b> Every address has to be on {domain}.</>
        );
      }
      case "missing_keyword":
        return "Enter a word or phrase to look for.";
      case "too_many_keywords":
        return <><b>Up to {p.limit || 5} keywords per scan.</b> Remove some to continue.</>;
      default:
        return p.message || "Check this field and try again.";
    }
  };

  const problemCard = (p) => {
    const host = hostOf(p.url);
    const primary = (label, onClick) => (
      <button type="button" className="scan-problem-btn" onClick={onClick}>{label}</button>
    );
    const ghost = (label, onClick) => (
      <button type="button" className="scan-problem-btn scan-problem-btn-ghost" onClick={onClick}>{label}</button>
    );
    switch (p.code) {
      case "robots_blocked":
        return (
          <ScanProblemCard icon="hand" title={`${host || "This site"} asks tools like WowOwl not to scan it`}
            actions={primary("Scan a different site", () => backToForm("url"))}>
            The site's owner set this in its robots.txt file, and WowOwl respects it.
          </ScanProblemCard>
        );
      case "scan_failed":
        return (
          <ScanProblemCard icon="tool" title="The scan stopped because of a problem on our side"
            actions={primary("Scan again", resubmit)}>
            {p.quotaRefunded ? "It wasn't counted toward your scans. " : ""}Running it again usually works.
          </ScanProblemCard>
        );
      case "poll_lost":
        return (
          <ScanProblemCard icon="wifi" title="We lost track of this scan"
            actions={p.taskId ? primary("Check again", () => checkScanAgain(p.taskId)) : null}>
            Your connection dropped while it was running. It may have finished already.
          </ScanProblemCard>
        );
      case "network":
        return (
          <ScanProblemCard icon="wifi" title="We can't reach WowOwl right now" actions={primary("Try again", resubmit)}>
            Check your internet connection. If it's working, our server may be restarting, so try again in a minute.
          </ScanProblemCard>
        );
      case "security_check":
        return (
          <ScanProblemCard icon="lock" title="Your browser's quick security check didn't finish"
            actions={primary("Refresh page", () => window.location.reload())}>
            Refresh the page and scan again. Signing in skips this check.
          </ScanProblemCard>
        );
      case "rate_limited":
        return (
          <ScanProblemCard icon="clock" title="That's a lot of scans in one minute">
            Wait a minute, then try again.
          </ScanProblemCard>
        );
      case "share_expired":
        return (
          <ScanProblemCard icon="link" tone="info" title="This shared result has expired"
            actions={primary("Run a new scan", () => {
              try { window.history.replaceState(null, "", window.location.pathname); } catch { /* ignore */ }
              backToForm("url");
            })}>
            Shared results are kept for 7 days. Run the scan yourself to see what the site says today.
          </ScanProblemCard>
        );
      case "cancelled":
        return <ScanProblemCard icon="info" tone="info" title="Scan cancelled" />;
      default:
        return (
          <ScanProblemCard icon="alert" title="This scan couldn't run"
            actions={<>{primary("Try again", resubmit)}{ghost("Scan a different site", () => backToForm("url"))}</>}>
            {p.message || "Please try again."}
          </ScanProblemCard>
        );
    }
  };

  // Some pages couldn't be opened (but not all of them). Shown above the
  // results, with or without matches.
  const failedNotice = (r) => {
    if (!r?.failed_pages || r.site_unreachable) return null;
    const n = r.failed_pages;
    return (
      <ResultNotice kind="warn" icon="alert">
        {n} page{n === 1 ? "" : "s"} couldn't be opened
        {typeof r.failed_status === "number" && <> (most answered <code>{statusLabel(r.failed_status)}</code>)</>}, so{" "}
        {n === 1 ? "it wasn't" : "they weren't"} searched.
      </ResultNotice>
    );
  };

  // Every page refused or failed: the site was down or blocked us. Not the
  // same as "no matches", which this used to say.
  const unreachableCard = (r) => {
    const why = unreachableReason(r.failed_status);
    return (
      <ScanProblemCard icon="door" title={`We couldn't open ${hostOf(r.base_url) || "this site"}`}
        actions={<>
          <button type="button" className="scan-problem-btn" onClick={resubmit}>Try again</button>
          <button type="button" className="scan-problem-btn scan-problem-btn-ghost" onClick={() => backToForm("url")}>
            Scan a different site
          </button>
        </>}
        fine={r.quota_refunded ? "This scan wasn't counted toward your limit." : null}>
        {why.lead}{why.status ? <> (it answered <code>{statusLabel(why.status)}</code>)</> : null}.{why.tail ? ` ${why.tail}` : ""}
      </ScanProblemCard>
    );
  };

  // "No matches", with how much was searched and the tips that fit this
  // scan's own settings.
  const noMatchesPanel = (r) => {
    const tried = r.scanned_pages?.length ?? r.debug_info?.total_scanned_pages ?? 0;
    const searched = Math.max(0, tried - (r.failed_pages || 0));
    const quoted = (r.search_words && r.search_words.length ? r.search_words : formWords()).map(w => `“${w}”`);
    const host = hostOf(r.base_url);
    const tips = [];
    const rescanWith = (change) => () => { change(); setRescanRequested(true); };
    if (r.page_cap_hit && r.total_site_pages && !r.is_shared) {
      const left = Math.max(0, r.total_site_pages - tried);
      tips.push({
        key: "cap",
        body: <><b>{left} page{left === 1 ? "" : "s"} weren't searched</b> because of the {user ? "Free" : "Guest"} limit
          of {r.max_pages}. The word may be on one of them.</>,
        action: { label: "See plans", run: () => {
          setPageCapInfo({ domain: host, total: r.total_site_pages, scanned: tried });
          setUpgradeReason("page_cap");
          setShowUpgradeModal(true);
        } },
      });
    }
    tips.push({
      key: "spell",
      body: r.use_regex
        ? "Check your pattern. Try it on text you know is on the site."
        : "Check the spelling, or try a shorter part of the word.",
    });
    if (r.strict_match && !r.is_shared) {
      tips.push({
        key: "exact",
        body: <><b>Exact match is on</b>, so longer forms of the word were skipped.</>,
        action: { label: "Scan without it", run: rescanWith(() => setStrictMatch(false)) },
      });
    }
    if (r.scopes && r.scopes.length < 4 && !r.is_shared) {
      tips.push({
        key: "scope",
        body: <>Only <b>{scopeList(r.scopes)}</b> {r.scopes.length === 1 ? "was" : "were"} searched.</>,
        action: { label: "Search the whole page", run: rescanWith(() => setScopes(["title", "headings", "meta", "body"])) },
      });
    }
    if (r.path_filter && !r.is_shared) {
      tips.push({
        key: "path",
        body: <>Only pages with <b>{r.path_filter}</b> in their address were searched.</>,
        action: { label: "Search the whole site", run: rescanWith(() => setPathFilter("")) },
      });
    }
    if (r.exclude_path && !r.is_shared) {
      tips.push({
        key: "exclude",
        body: <>Pages with <b>{r.exclude_path}</b> in their address were skipped.</>,
        action: { label: "Include them", run: rescanWith(() => setExcludePath("")) },
      });
    }
    if (tried > 0 && tried <= 2 && !r.sitemap_used && !r.bulk_mode) {
      tips.push({
        key: "few",
        body: <>We only found {tried} page{tried === 1 ? "" : "s"} on this site. Pages that appear only after clicking,
          searching or signing in can't be reached.</>,
      });
    }
    const title = r.cancelled
      ? "Scan cancelled before any matches were found"
      : r.timed_out
      ? `No matches for ${quoted.join(", ")} before the time limit`
      : `No matches for ${quoted.join(", ")}`;
    return (
      <div className="no-matches">
        <h3 className="no-matches-title">{title}</h3>
        <div className="no-matches-stats">
          <span><b>{searched}</b> page{searched === 1 ? "" : "s"} searched{host ? ` on ${host}` : ""}</span>
          {r.time_taken != null && <span><b>{formatDuration(r.time_taken)}</b></span>}
        </div>
        {r.timed_out && (
          <p className="no-matches-text">This site is very large, so the scan stopped at its 20-minute limit. Use
            “Limit to path” to scan one section fully.</p>
        )}
        {!r.cancelled && (
          <>
            <div className="no-matches-label">Things to try</div>
            <ul className="no-matches-tips">
              {tips.map(t => (
                <li key={t.key}>
                  <ResultIcon name="check" />
                  <span>{t.body}{t.action && <>{" "}
                    <button type="button" className="no-matches-link" onClick={t.action.run}>{t.action.label}</button></>}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        {!r.is_shared && (
          <div className="no-matches-actions">
            <button type="button" className="scan-problem-btn scan-problem-btn-ghost" onClick={() => backToForm("keyword")}>
              Change keyword
            </button>
            <button type="button" className="scan-problem-btn scan-problem-btn-ghost" onClick={() => backToForm("url")}>
              Scan another site
            </button>
          </div>
        )}
      </div>
    );
  };
  const formWords = () => words.map(normalizeKeyword).filter(Boolean);

  // Automatic JavaScript rendering note, shown above results (with or without
  // matches). The plan sentence is left out for someone viewing a shared result.
  const renderedBanner = (r) => {
    if (!r?.rendered_pages) return null;
    const n = r.rendered_pages;
    return (
      <ResultNotice icon="browser">
        This site builds its pages with JavaScript, so we opened <b>{n} page{n === 1 ? "" : "s"}</b> in a full
        browser to read {n === 1 ? "it" : "them"}.
        {r.render_cap_hit && !r.is_shared ? (
          <>
            {" "}Your {user ? "Free" : "Guest"} plan renders up to {r.render_limit} per scan, so some pages may be
            missing content.{" "}
            <a href="#pricing" onClick={(e) => { e.preventDefault(); setUpgradeReason("render_cap"); setShowUpgradeModal(true); }}>
              See options
            </a>
          </>
        ) : r.render_capped ? (
          <> We render up to {r.render_limit} pages per scan; the rest were read without JavaScript.</>
        ) : null}
      </ResultNotice>
    );
  };

  const historyStart = (historyPage - 1) * historyRowsPerPage;
  const historyGridAndPagination = (
    <>
      <div className="history-grid">
        {searchHistory
          .slice(historyStart, historyStart + historyRowsPerPage)
          .map((item, i) => (
            <div key={item.id ?? `${item.timestamp}-${historyStart + i}`} className="history-card">
              <div className="history-col-left">
                <p className="history-url" title={(item.urls || [item.url]).join(', ')}>
                  {item.url}
                  {item.urls?.length > 1 && (
                    <span className="history-multi-badge">+{item.urls.length - 1} more</span>
                  )}
                </p>
                <p className="history-time">🕐 {parseTimestamp(item.timestamp).toLocaleString()}</p>
              </div>
              <div className="history-keywords">
                {(item.words || [item.word]).filter(Boolean).map((kw, ki) => (
                  <span key={ki} className="history-kw-chip">{kw}</span>
                ))}
              </div>
              <div className="history-card-actions">
                <button className="history-scan-btn" onClick={() => handleSearchAgain(item)}>Scan again</button>
                <button className="history-delete-btn" onClick={() => handleDeleteSearch(historyStart + i)}>×</button>
              </div>
            </div>
          ))}
      </div>

      {totalHistoryPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button className="btn btn-outline-primary" disabled={historyPage === 1} onClick={() => handleHistoryPageChange(1)}>First</button>
          <button className="btn btn-outline-primary" disabled={historyPage === 1} onClick={() => handleHistoryPageChange(historyPage - 1)}>Previous</button>
          <span>Page {historyPage} of {totalHistoryPages || 1}</span>
          <button className="btn btn-outline-primary" disabled={historyPage === totalHistoryPages} onClick={() => handleHistoryPageChange(historyPage + 1)}>Next</button>
          <button className="btn btn-outline-primary" disabled={historyPage === totalHistoryPages} onClick={() => handleHistoryPageChange(totalHistoryPages)}>Last</button>
        </div>
      )}
    </>
  );

  // What Smart Search will actually send (capped), shared by the form preview
  // and handleSubmit so the two can't disagree.
  const smartScanWords = smartSearch
    ? smartSearchWords(words.map(normalizeKeyword).filter(Boolean), smartExpansions)
    : [];
  const smartScanSet = new Set(smartScanWords.map(w => w.toLowerCase()));

  // First keyword with an unquoted comma the visitor hasn't decided about yet.
  const commaChoiceIndex = words.findIndex(w =>
    commaParts(w).length > 0 && !phraseKeywords.has(w.trim().toLowerCase()));

  const searchCommaAsPhrase = (i) => {
    setPhraseKeywords(prev => new Set(prev).add(words[i].trim().toLowerCase()));
    setCommaNudge(false);
  };

  // Several keywords at once is Pro (or a credit), same as "+ Add keyword".
  const searchCommaAsSeparate = (i) => {
    if (!canUseProScan) {
      setUpgradeReason("multi_keyword");
      setShowUpgradeModal(true);
      return;
    }
    const lower = (x) => x.toLowerCase();
    const parts = commaParts(words[i]);
    const others = words.filter((_, j) => j !== i).map(normalizeKeyword).map(lower);
    const fresh = parts.filter((p, k) =>
      parts.findIndex(x => lower(x) === lower(p)) === k && !others.includes(lower(p)));
    const updated = [...words.slice(0, i), ...fresh, ...words.slice(i + 1)].filter(w => w.trim());
    const kept = updated.slice(0, 5);
    const dropped = updated.slice(5);
    setWords(kept.length ? kept : [""]);
    setCommaSplitNote(dropped.length
      ? `Up to 5 keywords per scan, so "${dropped.join('", "')}" ${dropped.length === 1 ? "was" : "were"} left out.`
      : "");
    setCommaNudge(false);
    const old = normalizeKeyword(words[i]);
    setSmartExpansions(ex => { const n = { ...ex }; delete n[old]; return n; });
    if (smartSearch) kept.forEach((w, j) => { if (fresh.includes(w)) fetchSmartExpansions(w, j); });
  };

  return (
    <>
      {/* Wrapped in an ErrorBoundary: this is a purely decorative effect
          with real, observed WebGL failure modes on some devices (older
          iPhones, iOS Low Power Mode, Private Browsing restrictions, Safari's
          per-page WebGL context limit) that SplashCursor itself now guards
          against internally — this boundary is the second, structural layer
          so that even an unanticipated future failure here (or in any other
          component that ends up first in this tree) can't take the whole
          site down to a blank page again, the way it did before either fix
          existed. */}
      <ErrorBoundary>
        {splashStarted && (
        <React.Suspense fallback={null}>
        {darkMode ? (
          <SplashCursor
            key="dark"
            DENSITY_DISSIPATION={2}
            VELOCITY_DISSIPATION={1.5}
            PRESSURE={0.05}
            CURL={1}
            SPLAT_RADIUS={0.09}
            SPLAT_FORCE={3000}
            COLOR_UPDATE_SPEED={10}
            SHADING
            RAINBOW_MODE
            COLOR="#504f50"
          />
        ) : (
          /* Went through several single fixed colors first (#272727 nearly
             invisible, #FF4FA3 hot pink too neon, --accent green too faint,
             terracotta/camel/deep rust all fine) before landing on picking
             one at random from that same earthy set per page load instead
             of committing to a single one - see LIGHT_SPLASH_COLORS and
             lightSplashColor above. RAINBOW_MODE stays false since that
             cycles per splat; this picks once and holds for the visit. */
          <SplashCursor
            key="light"
            DENSITY_DISSIPATION={2}
            VELOCITY_DISSIPATION={1.5}
            PRESSURE={0.05}
            CURL={1}
            SPLAT_RADIUS={0.09}
            SPLAT_FORCE={3000}
            COLOR_UPDATE_SPEED={10}
            SHADING
            RAINBOW_MODE={false}
            COLOR={lightSplashColor}
          />
        )}
        </React.Suspense>
        )}
      </ErrorBoundary>
      {showUpgradeModal && (
        <UpgradeModal
          reason={upgradeReason}
          user={user}
          countryCode={visitorCountryCode}
          pricingTable={pricingTable}
          onUpgrade={handleUpgrade}
          onBuyCredits={handleBuyCredits}
          onLogin={handleLogin}
          onClose={() => setShowUpgradeModal(false)}
          pageCapInfo={pageCapInfo}
        />
      )}
      {showScheduleModal && (
        <ScheduleModal
          user={user}
          onClose={() => setShowScheduleModal(false)}
          onUpgrade={() => { setShowScheduleModal(false); setUpgradeReason("schedules"); setShowUpgradeModal(true); }}
        />
      )}
      {showComparePicker && (
        <ComparePicker
          searchHistory={searchHistory}
          currentUrl={urls[0]}
          onPick={handlePickBaseline}
          onClose={() => { setShowComparePicker(false); setCompareError(null); }}
          fetchingId={compareFetchingId}
          compareError={compareError}
        />
      )}

      {/* Header Bar */}
      <Header darkMode={darkMode} setDarkMode={setDarkMode} user={user} onLogin={handleLogin} onLogout={handleLogout} onManageBilling={handleManageBilling} />
      <RenewalReminder user={user} onPay={handleManageBilling} />

      {/* Hero Banner Section */}

      <HeroBanner onScrollDown={() => scrollToSection("down")} />
      <section id="scan" className="features-section full-section">
        {/* Main Layout */}
        <div className="main-layout">
          {/* Main Content */}
          <div className="content">
            <div className="scanner-heading-block">
              <p className="section-eyebrow">Scanner</p>
              <h2 className="scanner-heading">Point it at a domain.</h2>
            </div>
            <div
              className={`form-container ${
                results || searchHistory.length > 0 ? "shift-up" : "center-vh"
              } form-container-editorial`}
            >
              {/* Input Section */}
              <form onSubmit={handleSubmit} className="mb-4">
                {!bulkMode && (
                  <div className="editorial-field-label-row">
                    <span>Website</span>
                    <span>{urls.length} / 3</span>
                  </div>
                )}
                {/* URL rows */}
                {!bulkMode && (
                  <>
                    <div className="d-flex gap-2 mb-2 align-items-center">
                      <div className={`input-group flex-grow-1${placement === "url" ? " has-problem" : ""}`} style={{ minWidth: 0 }}>
                        <span className="input-group-text"><FaGlobe /></span>
                        <input
                          type="text"
                          id="scan-url-input"
                          className="form-control"
                          placeholder="URL"
                          aria-invalid={placement === "url" || undefined}
                          value={urls[0]}
                          onChange={(e) => { const u = [...urls]; u[0] = e.target.value; setUrls(u); clearFieldProblem("url"); }}
                        />
                      </div>
                      {urls.length < 3 && !results && multiResults.length === 0 && (
                        <button
                          type="button"
                          className="keyword-add-btn url-add-btn"
                          onClick={() => {
                            if (!user || user.plan !== "pro") { setUpgradeReason("compare_urls"); setShowUpgradeModal(true); return; }
                            setUrls([...urls, ""]);
                          }}
                        >
                          <span className="url-add-full">+ Add URL</span>
                          <span className="url-add-short">+</span>
                          {(!user || user.plan !== "pro") && <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span>}
                        </button>
                      )}
                    </div>
                    {urls.slice(1).map((u, i) => (
                      <div key={i + 1}>
                        <div className="url-vs-divider">
                          <span className="url-vs-badge">vs</span>
                        </div>
                        <div className="d-flex gap-2 mb-2 align-items-center">
                          <div className="input-group flex-grow-1">
                            <span className="input-group-text"><FaGlobe /></span>
                            <input
                              type="text"
                              className="form-control"
                              placeholder={`Enter URL ${i + 2}…`}
                              value={u}
                              onChange={(e) => {
                                const updated = [...urls];
                                updated[i + 1] = e.target.value;
                                setUrls(updated);
                                clearFieldProblem("url");
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="history-delete-btn"
                            onClick={() => setUrls(urls.filter((_, j) => j !== i + 1))}
                            aria-label="Remove URL"
                          >×</button>
                        </div>
                      </div>
                    ))}
                    {placement === "url" && <FieldProblem>{fieldProblemMessage(problem)}</FieldProblem>}
                  </>
                )}

                {/* Bulk URL mode */}
                {bulkMode && (
                  <div className="mb-2">
                    <div className="d-flex gap-2 align-items-start">
                      <div style={{ flex: 1 }}>
                        <textarea
                          id="scan-bulk-input"
                          className={`form-control${placement === "url" ? " has-problem" : ""}`}
                          rows={5}
                          placeholder={"Paste URLs here, one per line:\nhttps://example.com/page-1\nhttps://example.com/page-2"}
                          value={bulkUrlsText}
                          aria-invalid={placement === "url" || undefined}
                          onChange={e => { setBulkUrlsText(e.target.value); clearFieldProblem("url"); }}
                          style={{ fontFamily: "monospace", fontSize: "13px", resize: "vertical" }}
                        />
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "6px" }}>
                          {(() => {
                            const count = bulkUrlsText.split("\n").map(u => u.trim()).filter(Boolean).length;
                            return count > 0 ? (
                              <span style={{ fontSize: "12px", color: "var(--accent, #4caf50)", fontWeight: 600 }}>
                                {count} URL{count !== 1 ? "s" : ""} ready to scan
                              </span>
                            ) : null;
                          })()}
                          <label style={{ fontSize: "12px", color: "#888", cursor: "pointer", marginLeft: "auto" }}>
                            <input
                              type="file"
                              accept=".txt,.csv"
                              style={{ display: "none" }}
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = ev => setBulkUrlsText(prev => {
                                  const extra = ev.target.result || "";
                                  return (prev.trim() ? prev.trim() + "\n" + extra.trim() : extra.trim());
                                });
                                reader.readAsText(file);
                                e.target.value = "";
                              }}
                            />
                            📂 Upload .txt / .csv
                          </label>
                        </div>
                        {placement === "url" && <FieldProblem>{fieldProblemMessage(problem)}</FieldProblem>}
                      </div>
                      <button
                        type="submit"
                        className={`btn ${(results || multiResults.length > 0) ? "btn-warning" : "btn-primary"}`}
                        style={{ whiteSpace: 'nowrap', minWidth: '80px', alignSelf: 'flex-start' }}
                        disabled={loading}
                        onClick={(results || multiResults.length > 0) ? handleReset : undefined}
                      >
                        {loading ? "Scanning…" : (results || multiResults.length > 0) ? "Reset" : "Scan"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Row 2 — Keywords */}
                <div className="editorial-field-label-row">
                  <span>Keyword</span>
                  <span>{words.length} / 5</span>
                </div>
                <div className="keywords-row mb-2">
                  {words.map((w, i) => (
                    <div key={i} className="keyword-chip">
                      <input
                        type="text"
                        className="keyword-chip-input"
                        placeholder={i === 0 ? "Keyword…" : `Keyword ${i + 1}…`}
                        value={w}
                        onChange={(e) => {
                          const updated = [...words];
                          const prev = updated[i];
                          updated[i] = e.target.value;
                          setWords(updated);
                          // Clear old expansion when word changes (keys are trimmed)
                          if (prev && normalizeKeyword(prev) !== normalizeKeyword(e.target.value)) {
                            setSmartExpansions(ex => { const n = { ...ex }; delete n[normalizeKeyword(prev)]; return n; });
                          }
                          setCommaNudge(false);
                          setCommaSplitNote("");
                          clearFieldProblem("keyword");
                          if (smartSearch) fetchSmartExpansions(e.target.value, i);
                        }}
                      />
                      {words.length > 1 && (
                        <button
                          type="button"
                          className="keyword-chip-remove"
                          onClick={() => {
                            const removed = normalizeKeyword(words[i]);
                            setWords(words.filter((_, j) => j !== i));
                            // Its related words would otherwise still be scanned.
                            if (removed && !words.some((x, j) => j !== i && normalizeKeyword(x) === removed)) {
                              setSmartExpansions(ex => { const n = { ...ex }; delete n[removed]; return n; });
                            }
                          }}
                          aria-label="Remove keyword"
                        >×</button>
                      )}
                    </div>
                  ))}
                  {words.length < 5 && (
                    <button
                      type="button"
                      className="keyword-add-btn"
                      onClick={() => {
                        if (!canUseProScan) {
                          setUpgradeReason("multi_keyword");
                          setShowUpgradeModal(true);
                        } else {
                          setWords([...words, ""]);
                        }
                      }}
                      title={!canUseProScan ? "Pro feature — scan for multiple keywords" : ""}
                    >+ Add keyword {!canUseProScan && <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span>}</button>
                  )}
                </div>

                {placement === "keyword" && <FieldProblem>{fieldProblemMessage(problem)}</FieldProblem>}

                {/* "one, two": a list or a phrase? Ask rather than guess. */}
                {commaChoiceIndex !== -1 && (() => {
                  const typed = words[commaChoiceIndex].trim();
                  const parts = commaParts(typed).map(p => `"${p}"`);
                  const listed = parts.length > 2
                    ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
                    : parts.join(" and ");
                  return (
                    <div className={`comma-choice${commaNudge ? " comma-choice-nudge" : ""}`}
                      role="group" aria-label="How to search this keyword">
                      <div className="comma-choice-q">
                        Search for <strong>"{typed}"</strong> as:
                      </div>
                      <div className="comma-choice-options">
                        <button type="button" className="comma-choice-btn"
                          onClick={() => searchCommaAsSeparate(commaChoiceIndex)}>
                          <span className="comma-choice-title">
                            Separate words
                            {!canUseProScan && <span className="comma-choice-pro">PRO</span>}
                          </span>
                          <span className="comma-choice-sub">{listed}</span>
                        </button>
                        <button type="button" className="comma-choice-btn"
                          onClick={() => searchCommaAsPhrase(commaChoiceIndex)}>
                          <span className="comma-choice-title">One exact phrase</span>
                          <span className="comma-choice-sub">"{typed}"</span>
                        </button>
                      </div>
                      {commaNudge && <div className="comma-choice-hint">Pick one to start the scan.</div>}
                      <div className="comma-choice-tip">Tip: put a phrase in "quotes" and we won't ask.</div>
                    </div>
                  );
                })()}
                {commaSplitNote && <div className="comma-choice-note">{commaSplitNote}</div>}

                {/* Smart Search expansions */}
                {smartSearch && Object.keys(smartExpansions).length > 0 && (
                  <div style={{ marginBottom: "8px", padding: "10px 12px", borderRadius: "10px",
                    border: "1px dashed #a5d6a7", background: "#f1faf1" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--accent, #4caf50)",
                      letterSpacing: "0.06em", marginBottom: "8px" }}>
                      ALSO SEARCHING FOR
                    </div>
                    {Object.entries(smartExpansions).map(([seed, { suggestions }]) => (
                      <div key={seed} style={{ marginBottom: "6px", display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
                        <span style={{ fontSize: "11px", color: "#888", marginRight: "2px" }}>
                          {seed} →
                        </span>
                        {suggestions.map((s, idx) => {
                          const overLimit = s.selected && !smartScanSet.has(s.word.toLowerCase());
                          return (
                          <button
                            key={s.word}
                            title={overLimit ? `Not scanned: Smart Search covers up to ${SMART_SEARCH_MAX_KEYWORDS} keywords` : undefined}
                            type="button"
                            onClick={() => setSmartExpansions(prev => ({
                              ...prev,
                              [seed]: {
                                suggestions: prev[seed].suggestions.map((x, i) =>
                                  i === idx ? { ...x, selected: !x.selected } : x
                                )
                              }
                            }))}
                            style={{
                              padding: "3px 10px", borderRadius: "20px", fontSize: "12px",
                              border: overLimit ? "1.5px dashed #bbb" : s.selected ? "1.5px solid var(--accent, #4caf50)" : "1.5px solid #ddd",
                              background: s.selected && !overLimit ? "#e8f5e9" : "#fff",
                              color: overLimit ? "#999" : s.selected ? "#2e7d32" : "#aaa",
                              cursor: "pointer", fontWeight: s.selected && !overLimit ? 600 : 400,
                              transition: "all 0.15s",
                            }}
                          >
                            {s.selected && !overLimit ? "✓ " : ""}{s.word}
                          </button>
                          );
                        })}
                      </div>
                    ))}
                    <div style={{ fontSize: "11px", color: "#aaa", marginTop: "4px" }}>
                      Scanning {smartScanWords.length} of up to {SMART_SEARCH_MAX_KEYWORDS} keywords. Click a word to include or exclude it.
                    </div>
                  </div>
                )}

                {/* Row 3 — Advanced options trigger */}
                <div className="advanced-trigger-row advanced-trigger-row-editorial">
                  <div className="advanced-triggers-group">
                    <button
                      type="button"
                      className={`advanced-trigger-btn ${showAdvanced ? "open" : ""}`}
                      onClick={() => { setShowAdvanced(p => !p); setShowHistoryPanel(false); }}
                    >
                      <span className="advanced-trigger-icon">⚙</span>
                      Advanced options
                      {(strictMatch || useRegex || smartSearch || revealFilters || pathFilter || excludePath || scopes.length < 4 || maxPages) && (
                        <span className="advanced-active-dot" title="Some options are active" />
                      )}
                      <span className="advanced-trigger-caret">{showAdvanced ? "▲" : "▼"}</span>
                    </button>
                    {searchHistory.length > 0 && (
                      <button
                        type="button"
                        className={`advanced-trigger-btn ${showHistoryPanel ? "open" : ""}`}
                        onClick={() => { setShowHistoryPanel(p => !p); setShowAdvanced(false); }}
                      >
                        <span className="advanced-trigger-icon">🕐</span>
                        Recent scans ({searchHistory.length})
                        <span className="advanced-trigger-caret">{showHistoryPanel ? "▲" : "▼"}</span>
                      </button>
                    )}
                  </div>
                  {!bulkMode && (
                    <button
                      type="submit"
                      className="editorial-scan-btn"
                      disabled={loading || multiResults.some(s => s.loading)}
                      onClick={(results || multiResults.length > 0) ? handleReset : undefined}
                    >
                      {(loading || multiResults.some(s => s.loading)) ? "Scanning…" : (results || multiResults.length > 0) ? "Reset" : "Scan →"}
                    </button>
                  )}
                </div>

                {/* Advanced panel */}
                {showAdvanced && (
                  <div className="advanced-panel">

                    {/* Bulk URL scan */}
                    {!results && multiResults.length === 0 && (
                      <>
                        <div className="advanced-row">
                          <div className="advanced-row-left">
                            <span className="advanced-row-title">📋 Bulk URL scan <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span></span>
                            <span className="advanced-row-desc">Paste or upload a list of URLs and scan them all in one run</span>
                          </div>
                          <button
                            type="button"
                            className="editorial-row-action-btn"
                            onClick={() => {
                              if (bulkMode) { setBulkMode(false); setBulkUrlsText(""); setUrls([""]); return; }
                              if (!canUseProScan) { setUpgradeReason("bulk_upload"); setShowUpgradeModal(true); return; }
                              setBulkMode(true);
                            }}
                          >
                            {bulkMode ? "← Domain scan" : "Open →"}
                          </button>
                        </div>
                        <div className="advanced-divider" />
                      </>
                    )}

                    {/* ── FREE options first ── */}

                    {/* Exact Match — available to all */}
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">Exact Match</span>
                        <span className="advanced-row-desc">Only count whole-word matches (e.g. "ace" won't match "place")</span>
                      </div>
                      <div
                        className={`exact-match-toggle ${strictMatch && !useRegex ? "active" : ""}`}
                        onClick={() => { if (!useRegex) setStrictMatch(prev => !prev); }}
                        role="switch"
                        aria-checked={strictMatch && !useRegex}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === ' ' && !useRegex) setStrictMatch(prev => !prev); }}
                        style={useRegex ? { opacity: 0.35, cursor: "not-allowed" } : {}}
                        title={useRegex ? "Disabled when Regex mode is on" : ""}
                      >
                        <span className="exact-match-knob" />
                      </div>
                    </div>

                    <div className="advanced-divider" />

                    {/* Max pages — guest capped at 100, free at 200, Pro uncapped */}
                    {(() => {
                      const pageCap = !user ? 100 : user.plan === "pro" ? Infinity : 200;
                      const lockBadge = (
                        <span style={{ fontSize: "9px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 4px", marginLeft: "4px" }}>PRO</span>
                      );
                      return (
                        <div className="advanced-row">
                          <div className="advanced-row-left">
                            <span className="advanced-row-title">Max pages</span>
                            <span className="advanced-row-desc">Stop after scanning this many pages — useful for large sites</span>
                          </div>
                          <div className="max-pages-group">
                            {[50, 100, 200, 500].map(n => {
                              // Credit holders can pick above their cap — that scan spends a
                              // credit. "All" below stays Pro-only: it's also the default
                              // (null = no choice made), which for a free user means their cap.
                              const locked = n > pageCap && !canUseProScan;
                              return (
                                <button
                                  key={n}
                                  type="button"
                                  className={`scope-chip ${maxPages === n ? "active" : ""}`}
                                  style={locked ? { opacity: 0.5 } : {}}
                                  onClick={() => {
                                    if (locked) { setUpgradeReason("max_pages"); setShowUpgradeModal(true); return; }
                                    setMaxPages(maxPages === n ? null : n);
                                  }}
                                >
                                  {n}{locked && n >= 500 && lockBadge}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              className={`scope-chip ${maxPages === null ? "active" : ""}`}
                              style={pageCap !== Infinity ? { opacity: 0.5 } : {}}
                              onClick={() => {
                                if (pageCap !== Infinity) { setUpgradeReason("max_pages"); setShowUpgradeModal(true); return; }
                                setMaxPages(null);
                              }}
                            >
                              All{pageCap !== Infinity && lockBadge}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="advanced-divider" />

                    {/* ── PRO options ── */}

                    {/* Regex search */}
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">Regex search <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span></span>
                        <span className="advanced-row-desc">Treat keywords as regular expressions — e.g. <code>pric(e|ing)</code> or <code>\d+ results</code></span>
                      </div>
                      <div
                        className={`exact-match-toggle ${useRegex ? "active" : ""}`}
                        onClick={() => {
                          if (!canUseProScan) { setUpgradeReason("advanced_match"); setShowUpgradeModal(true); return; }
                          setUseRegex(prev => !prev);
                        }}
                        role="switch"
                        aria-checked={useRegex}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === ' ') { if (!canUseProScan) { setUpgradeReason("advanced_match"); setShowUpgradeModal(true); } else setUseRegex(prev => !prev); } }}
                      >
                        <span className="exact-match-knob" />
                      </div>
                    </div>

                    <div className="advanced-divider" />

                    {/* Reveal filtered content */}
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">
                          Reveal filtered content
                          <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span>
                        </span>
                        <span className="advanced-row-desc">
                          For pages that are blank until you pick a dropdown/date filter — we'll try applying one automatically so that content gets scanned too. Uses full browser rendering, so it can add real time per page.
                        </span>
                      </div>
                      <div
                        className={`exact-match-toggle ${revealFilters ? "active" : ""}`}
                        onClick={() => {
                          if (!canUseProScan) { setUpgradeReason("reveal_filters"); setShowUpgradeModal(true); return; }
                          setRevealFilters(prev => !prev);
                        }}
                        role="switch"
                        aria-checked={revealFilters}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === ' ') { if (!canUseProScan) { setUpgradeReason("reveal_filters"); setShowUpgradeModal(true); } else setRevealFilters(prev => !prev); } }}
                      >
                        <span className="exact-match-knob" />
                      </div>
                    </div>

                    <div className="advanced-divider" />

                    {/* Search scope — PRO */}
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">Search in <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span></span>
                        <span className="advanced-row-desc">Which parts of each page to search</span>
                      </div>
                      <div className="scope-chips-group" onClick={() => { if (!canUseProScan) { setUpgradeReason("search_scopes"); setShowUpgradeModal(true); } }}>
                        {[
                          { key: "title",    label: "Page Title",  icon: "T" },
                          { key: "headings", label: "H1 / H2 / H3", icon: "H" },
                          { key: "meta",     label: "Meta Description", icon: "M" },
                          { key: "body",     label: "Body Text",   icon: "B" },
                        ].map(({ key, label, icon }) => (
                          <button
                            key={key}
                            type="button"
                            className={`scope-chip ${scopes.includes(key) ? "active" : ""} ${!canUseProScan ? "locked" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!canUseProScan) { setUpgradeReason("search_scopes"); setShowUpgradeModal(true); return; }
                              toggleScope(key);
                            }}
                            style={!canUseProScan ? { cursor: "pointer", opacity: 0.5 } : {}}
                          >
                            <span className="scope-chip-icon">{icon}</span>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="advanced-divider" />

                    {/* Smart Search */}
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">
                          Smart Search
                          <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span>
                        </span>
                        <span className="advanced-row-desc">
                          Auto-expands keywords with synonyms & variants — e.g. <em>price</em> → <em>pricing, cost, value</em>
                        </span>
                      </div>
                      <div
                        className={`exact-match-toggle ${smartSearch ? "active" : ""}`}
                        onClick={() => {
                          if (!canUseProScan) { setUpgradeReason("smart_search"); setShowUpgradeModal(true); return; }
                          const next = !smartSearch;
                          setSmartSearch(next);
                          if (!next) setSmartExpansions({});
                          else words.forEach((w, i) => fetchSmartExpansions(w, i));
                        }}
                        role="switch"
                        aria-checked={smartSearch}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === ' ') e.currentTarget.click(); }}
                      >
                        <span className="exact-match-knob" />
                      </div>
                    </div>

                    <div className="advanced-divider" />

                    {/* Path filter */}
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">Limit to path <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span></span>
                        <span className="advanced-row-desc">Only scan URLs containing this path — e.g. <code>/blog/</code> or <code>/products/</code></span>
                      </div>
                      <input
                        type="text"
                        className="path-filter-input"
                        placeholder="/blog/"
                        value={pathFilter}
                        readOnly={!canUseProScan}
                        onClick={() => { if (!canUseProScan) { setUpgradeReason("path_filter"); setShowUpgradeModal(true); } }}
                        onChange={e => setPathFilter(e.target.value)}
                        style={!canUseProScan ? { cursor: "pointer", background: "#f5f5f5", color: "#bbb" } : {}}
                      />
                    </div>

                    <div className="advanced-divider" />

                    {/* Exclude path */}
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">Exclude path <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span></span>
                        <span className="advanced-row-desc">Skip URLs containing this path — e.g. <code>/blog/</code> or <code>/products/</code></span>
                      </div>
                      <input
                        type="text"
                        className="path-filter-input"
                        placeholder="/blog/"
                        value={excludePath}
                        readOnly={!canUseProScan}
                        onClick={() => { if (!canUseProScan) { setUpgradeReason("path_filter"); setShowUpgradeModal(true); } }}
                        onChange={e => setExcludePath(e.target.value)}
                        style={!canUseProScan ? { cursor: "pointer", background: "#f5f5f5", color: "#bbb" } : {}}
                      />
                    </div>

                    {/* Schedule recurring scan — visible to all, PRO gated */}
                    <div className="advanced-divider" />
                    <div className="advanced-row">
                      <div className="advanced-row-left">
                        <span className="advanced-row-title">🗓️ Schedule recurring scan <span style={{ fontSize: "10px", background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", borderRadius: "4px", padding: "1px 5px", marginLeft: "4px" }}>PRO</span></span>
                        <span className="advanced-row-desc">Set up automatic scans — daily or weekly — with results sent to your email</span>
                      </div>
                      <div
                        className={`exact-match-toggle ${showScheduleModal ? "active" : ""}`}
                        onClick={() => {
                          if (!user) { handleLogin(); return; }
                          if (user.plan !== "pro") { setUpgradeReason("schedules"); setShowUpgradeModal(true); return; }
                          setShowScheduleModal(prev => !prev);
                        }}
                        role="switch"
                        aria-checked={showScheduleModal}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === ' ') e.currentTarget.click(); }}
                      >
                        <span className="exact-match-knob" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Scans panel — trigger now lives next to Advanced Options
                    above; this just renders its content when toggled open.
                    Classic mode keeps its own always-visible section below the card. */}
                {searchHistory.length > 0 && showHistoryPanel && (
                  <div className="advanced-panel history-inline-panel">
                    <div className="history-inline-panel-header">
                      <span className="advanced-row-title">Recent Scans</span>
                      <button className="history-clear-btn" onClick={handleDeleteHistory}>Clear All</button>
                    </div>
                    {historyGridAndPagination}
                  </div>
                )}

                {/* Credit holders: say up front whether this scan will spend a credit.
                    Mirrors the backend's _requested_pro_feature + page-cap check. */}
                {creditBalance > 0 && !loading && (() => {
                  const usesProOption =
                    words.filter(w => w.trim()).length > 1 || useRegex || smartSearch || revealFilters ||
                    pathFilter.trim() || excludePath.trim() || bulkMode || scopes.length < 4 ||
                    (maxPages && maxPages > 200);
                  const plural = creditBalance === 1 ? "credit" : "credits";
                  return (
                    <div className="credit-scan-note">
                      {usesProOption ? (
                        <>🎟️ This scan uses Pro options — it'll use <strong>1 of your {creditBalance} scan {plural}</strong>. Your free daily scans stay untouched.</>
                      ) : (
                        <>🎟️ You have <strong>{creditBalance} scan {plural}</strong> — Pro options (multiple keywords, regex, path filters, bulk, 500 pages) are unlocked. A scan only uses a credit if it uses one of them.</>
                      )}
                    </div>
                  );
                })()}

                {/* Scan progress */}
                {(loading || (multiResults.length > 0 && multiResults[activeTabIdx]?.loading)) && (
                  <div className="scan-progress-box">
                    <div className="scan-progress-pulse" />
                    {scanProgress ? (
                      <>
                        <div className="scan-progress-main">
                          Scanning
                          <span className="scan-progress-count"> {scanProgress.scanned}</span>
                          {scanProgress.max_pages ? ` / ${scanProgress.max_pages}` : ""} pages
                        </div>
                        {scanProgress.found < scanProgress.scanned && (
                          <div className="scan-progress-sub">
                            {scanProgress.found} page{scanProgress.found !== 1 ? "s" : ""} with keyword matches so far
                          </div>
                        )}
                        {scanProgress.rendered > 0 && (
                          <div className="scan-progress-sub">
                            This site needs JavaScript: {scanProgress.rendered} page{scanProgress.rendered !== 1 ? "s" : ""} opened
                            in a full browser so far, which takes a little longer
                          </div>
                        )}
                        {scanProgress.max_pages && (
                          <div className="scan-progress-bar-wrap">
                            <div
                              className="scan-progress-bar-fill"
                              style={{ width: `${Math.min(100, (scanProgress.scanned / scanProgress.max_pages) * 100)}%` }}
                            />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="scan-progress-main">Starting scan…</div>
                    )}
                    <button
                      type="button"
                      className="scan-cancel-btn"
                      onClick={handleCancelScan}
                      disabled={cancelling}
                    >
                      {cancelling ? "Cancelling…" : "Cancel scan"}
                    </button>
                  </div>
                )}
              </form>
            </div>
            {/* Results Section */}

            {showNotificationPopup && (
              <div className="notification-popup">
                <div className="popup-content">
                  <h4>Get notified when it's done?</h4>
                  <p>
                    Your scan has started. Allow browser notifications and
                    we'll let you know the moment it finishes — handy for
                    large sites.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      // Either answer counts as "asked" — the scan is already
                      // running either way, and whatever the browser prompt
                      // returns, we don't ask again.
                      dismissNotifPrompt();
                      // Older Safari's requestPermission is callback-only and
                      // returns undefined, so don't assume a Promise.
                      if (supportsNotifications) {
                        try { Notification.requestPermission()?.catch?.(() => {}); } catch {}
                      }
                    }}
                  >
                    Allow Notifications
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={dismissNotifPrompt}
                  >
                    Skip
                  </button>
                </div>
              </div>
            )}

            {/* Multi-URL tabs */}
            {multiResults.length > 1 && (
              <div className="url-tabs-bar">
                {multiResults.map((scan, i) => {
                  let hostname = scan.url;
                  try { hostname = new URL(scan.url).hostname; } catch {}
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`url-tab-btn ${activeTabIdx === i ? "active" : ""}`}
                      onClick={() => setActiveTabIdx(i)}
                    >
                      {hostname}
                      {scan.loading && <span className="url-tab-spinner"> ⟳</span>}
                      {!scan.loading && scan.result && (
                        <span className="url-tab-count">
                          {pagesScanned(scan.result) === null
                            ? `${pagesWithMatches(scan.result)} pages matched`
                            : `${pagesWithMatches(scan.result)} of ${pagesScanned(scan.result)} pages`}
                        </span>
                      )}
                      {!scan.loading && scan.error && <span className="url-tab-error"> !</span>}
                    </button>
                  );
                })}
                {multiResults.some(s => !s.loading && s.result) && (
                  <button
                    type="button"
                    className={`btn-share ${shareAllCopied ? "copied" : ""}`}
                    style={{ marginLeft: 'auto' }}
                    onClick={() => {
                      if (!user || user.plan !== "pro") { setUpgradeReason("share"); setShowUpgradeModal(true); return; }
                      handleShareAll();
                    }}
                    title={(!user || user.plan !== "pro") ? "Pro feature — share all results" : "Copy link to all results"}
                  >
                    {shareAllCopied ? "✓ Link copied!" : "🔗 Share all"}
                    {(!user || user.plan !== "pro") && <span style={{ fontSize: "10px", marginLeft: "4px" }}>🔒</span>}
                  </button>
                )}
              </div>
            )}

            {/* Fresh Scan Button and Cached Message */}
            {results && results.is_cached === true && (
              <p className="cached-notice">
                Showing cached results. <button className="cached-refresh-btn" onClick={(e) => handleSubmit(e, true)} disabled={loading}>Run fresh scan →</button>
              </p>
            )}

            {schemeAlt ? (
              // Requested scheme was blocked by robots.txt, but the other scheme is allowed
              <div className="alert alert-warning text-center">
                {schemeAlt.message}{" "}
                <button
                  className="cached-refresh-btn"
                  onClick={(e) => handleSubmit(e, false, schemeAlt.suggestedUrl)}
                >
                  Scan {schemeAlt.suggestedUrl} instead →
                </button>
              </div>
            ) : placement === "card" ? (
              problemCard(problem)
            ) : results && results.table.length > 0 ? (
              // Display the results table if valid data exists
              <div className="results-area">
                <div className="r-notices">
                  {results.is_shared && (
                    <ResultNotice icon="info">
                      You're viewing a shared scan result. <a href="/">Run your own scan →</a>
                    </ResultNotice>
                  )}
                  {results.cancelled && (
                    <ResultNotice kind="warn" icon="alert">
                      Scan cancelled. These are the pages found before you stopped it.
                    </ResultNotice>
                  )}
                  {results.timed_out && (
                    <ResultNotice kind="warn" icon="clock">
                      This site is very large, so the scan stopped at its 20-minute time limit. These are the
                      pages found up to that point. Use “Limit to path” or a lower page limit to cover one
                      section fully.
                    </ResultNotice>
                  )}
                  {results.regex_timeout_pages > 0 && (
                    <ResultNotice kind="warn" icon="alert">
                      Your regex pattern was too slow to evaluate on {results.regex_timeout_pages} page
                      {results.regex_timeout_pages === 1 ? "" : "s"}, so those pages were skipped for it. A simpler
                      pattern (avoid nested repeats like <code>(a+)+</code>) will cover them.
                    </ResultNotice>
                  )}
                  {renderedBanner(results)}
                  {failedNotice(results)}
                  {results.dynamic_pages_capped && (
                    <ResultNotice kind="warn" icon="alert">
                      This site uses JavaScript rendering, so we scanned the first {results.max_pages} pages.{" "}
                      <a href="#pricing" onClick={(e) => { e.preventDefault(); setUpgradeReason("dynamic_pages"); setShowUpgradeModal(true); }}>
                        Upgrade to Pro
                      </a>{" "}
                      to scan every page of dynamic websites.
                    </ResultNotice>
                  )}
                </div>
                {(() => {
                  const kwEntries = Object.entries(results.keyword_totals || {});
                  const scanned = pagesScanned(results);
                  const seconds = results.is_cached ? results.cache_fetch_time : scanTime;
                  const { host } = splitPageUrl(results.base_url || "");
                  return (
                    <div className="r-summary">
                      <div className="r-stat r-stat-lead">
                        <span className="r-stat-num">{(results.total_word_count || 0).toLocaleString()}</span>
                        <span className="r-stat-lbl">Matches</span>
                      </div>
                      <div className="r-stat">
                        <span className="r-stat-num">{pagesWithMatches(results).toLocaleString()}</span>
                        <span className="r-stat-lbl">Pages with matches</span>
                      </div>
                      {scanned !== null && (
                        <div className="r-stat">
                          <span className="r-stat-num">{scanned.toLocaleString()}</span>
                          <span className="r-stat-lbl">Pages scanned</span>
                        </div>
                      )}
                      <div className="r-stat">
                        <span className="r-stat-num">{formatDuration(seconds)}</span>
                        <span className="r-stat-lbl">{results.is_cached ? "From cache" : "Time taken"}</span>
                      </div>
                      <div className="r-summary-kw">
                        {kwEntries.length > 1
                          ? kwEntries.map(([kw, cnt], i) => (
                              <span key={kw}>{i > 0 && " · "}<b>{kw}</b> {cnt.toLocaleString()}</span>
                            ))
                          : <>Keyword <b>{kwEntries[0]?.[0] || (results.search_words || []).join(", ")}</b></>}
                        {host && <> on <b>{host}</b></>}
                      </div>
                    </div>
                  );
                })()}
                <div>
                {/* Actions row — free actions (Email, CSV) first, then Pro-only ones
                    after a divider, each with a PRO tag for non-Pro viewers. */}
                  <div className="r-actions">
                    <button
                      type="button"
                      className={`r-btn ${showEmailForm ? "on" : ""}`}
                      onClick={() => { setShowEmailForm(p => !p); setEmailFeedback(null); }}
                    >
                      <ResultIcon name="mail" />Email
                    </button>
                    <CSVLink
                      data={results.table.map(({ page, total_count, filters_applied }) => ({
                        // csvSafe: page URLs and filter labels come from the scanned
                        // site, so a cell starting with "=" mustn't run as a formula.
                        page: csvSafe(page),
                        total_count,
                        // Only added when the scan actually used the feature — an
                        // always-empty column on every export otherwise. Flattened
                        // to a single readable string since CSV has no nested-object
                        // representation; dropping this here would silently lose the
                        // one piece of info that distinguishes a filter-revealed
                        // match from a default-content one (same gap fixed for the
                        // live results table's own "Found under filter" display).
                        ...(results.reveal_filters ? {
                          filter_applied: filters_applied
                            ? csvSafe(Object.entries(filters_applied).map(([k, v]) => `${k} = ${v}`).join("; "))
                            : "",
                        } : {}),
                      }))}
                      filename="results.csv"
                      onClick={() => { trackEvent("csv_export", { user_tier: userTier }); trackFeatureUse("csv_export"); }}
                      className="r-btn"
                    >
                      <ResultIcon name="download" />CSV
                    </CSVLink>
                    <span className="r-actions-sep" aria-hidden="true" />
                    {results.share_id && multiResults.length === 0 && (
                      <button
                        type="button"
                        className={`r-btn r-btn-ghost ${shareCopied ? "on" : ""}`}
                        onClick={() => {
                          if ((!user || user.plan !== "pro")) { setUpgradeReason("share"); setShowUpgradeModal(true); return; }
                          handleShare();
                        }}
                        title={(!user || user.plan !== "pro") ? "Pro feature — share scan results" : "Copy shareable link"}
                      >
                        <ResultIcon name="link" />{shareCopied ? "Link copied" : "Share"}
                        {(!user || user.plan !== "pro") && <span className="r-pro-tag">PRO</span>}
                      </button>
                    )}
                    <button
                      type="button"
                      className={`r-btn r-btn-ghost ${compareBaseline ? "on" : ""}`}
                      onClick={() => {
                        if (compareBaseline) { setCompareBaseline(null); setDiffView(false); }
                        else if ((!user || user.plan !== "pro")) {
                          setUpgradeReason("compare");
                          setShowUpgradeModal(true);
                        } else {
                          setCompareError(null);
                          setShowComparePicker(true);
                        }
                      }}
                      title={(!user || user.plan !== "pro") ? "Pro feature — upgrade to compare scans" : compareBaseline ? "Clear comparison" : "Compare with a past scan"}
                    >
                      <ResultIcon name="compare" />{compareBaseline ? "Clear compare" : "Compare"}
                      {(!user || user.plan !== "pro") && !compareBaseline && <span className="r-pro-tag">PRO</span>}
                    </button>
                    <button
                      type="button"
                      className="r-btn r-btn-ghost"
                      onClick={() => {
                        if ((!user || user.plan !== "pro")) {
                          setUpgradeReason("pdf_export");
                          setShowUpgradeModal(true);
                          return;
                        }
                        loadPdfGenerator()
                          .then((generatePDF) => generatePDF({
                            results,
                            urls,
                            words,
                            scanTime,
                            logoSrc: Wowowl,
                          }))
                          .catch((err) => console.error("PDF export failed:", err));
                        trackFeatureUse("pdf_export");
                        trackEvent("pdf_export", { user_tier: userTier });
                      }}
                      title={(!user || user.plan !== "pro") ? "Pro feature — upgrade to download PDF" : "Download PDF report"}
                    >
                      <ResultIcon name="file" />PDF
                      {(!user || user.plan !== "pro") && <span className="r-pro-tag">PRO</span>}
                    </button>
                    <button
                      type="button"
                      className={`r-btn r-btn-ghost ${showNotFound ? "on" : ""}`}
                      onClick={() => {
                        if ((!user || user.plan !== "pro")) {
                          setUpgradeReason("not_found");
                          setShowUpgradeModal(true);
                          return;
                        }
                        setShowNotFound(p => !p);
                      }}
                      title={(!user || user.plan !== "pro") ? "Pro feature — see pages missing your keyword" : "Toggle pages where keyword was not found"}
                    >
                      <ResultIcon name="notfound" />Not found
                      {(!user || user.plan !== "pro")
                        ? <span className="r-pro-tag">PRO</span>
                        : (() => {
                            const foundSet = new Set(results.table.map(r => r.page));
                            const count = (results.scanned_pages || []).filter(u => !foundSet.has(u)).length;
                            return <span className="r-count-pill">{count}</span>;
                          })()
                      }
                    </button>
                  </div>
                </div>

                {showEmailForm && (
                  <div className="email-results-form">
                    <input
                      type="email"
                      className="email-results-input"
                      placeholder="Enter your email address…"
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleEmailResults()}
                      autoFocus
                    />
                    <button
                      className="email-results-send"
                      onClick={handleEmailResults}
                      disabled={emailSending}
                    >
                      {emailSending ? "Sending…" : "Send"}
                    </button>
                    {emailFeedback && (
                      <span className={`email-results-feedback ${emailFeedback.type}`}>
                        {emailFeedback.msg}
                      </span>
                    )}
                  </div>
                )}

                {/* Compare tab switcher */}
                {compareBaseline && (
                  <div style={{ display: "flex", gap: "0", marginBottom: "12px", borderBottom: "2px solid #e0e0e0" }}>
                    {[
                      { label: "Current Results", active: !diffView, onClick: () => setDiffView(false) },
                      { label: `⇄ Diff vs ${parseTimestamp(compareBaseline.historyItem.timestamp).toLocaleDateString()}`, active: diffView, onClick: () => setDiffView(true) },
                    ].map(tab => (
                      <button key={tab.label} type="button" onClick={tab.onClick}
                        style={{ padding: "8px 18px", border: "none", background: "none", cursor: "pointer",
                          fontWeight: tab.active ? 700 : 400, fontSize: "13px",
                          color: tab.active ? "#1565c0" : "#888",
                          borderBottom: tab.active ? "2px solid #1565c0" : "2px solid transparent",
                          marginBottom: "-2px", transition: "all 0.15s" }}>
                        {tab.label}
                      </button>
                    ))}
                    <div style={{ marginLeft: "auto", padding: "6px 0", fontSize: "11px", color: "#aaa", alignSelf: "center" }}>
                      Baseline: {compareBaseline.historyItem.url || compareBaseline.historyItem.urls?.[0]}
                    </div>
                  </div>
                )}

                {/* Diff summary chips */}
                {compareBaseline && diffView && (() => {
                  const diffRows = computeDiff(results, compareBaseline.result);
                  const counts = { new: 0, increased: 0, same: 0, decreased: 0, gone: 0 };
                  diffRows.forEach(r => counts[r.status]++);
                  return (
                    <>
                      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
                        {[
                          { key: "new",       label: `${counts.new} New`,       bg: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)" },
                          { key: "increased", label: `${counts.increased} ↑ Up`, bg: "#e8f5e9", color: "#2e7d32" },
                          { key: "same",      label: `${counts.same} = Same`,   bg: "#f5f5f5", color: "#888" },
                          { key: "decreased", label: `${counts.decreased} ↓ Down`, bg: "#fff8e1", color: "#e65100" },
                          { key: "gone",      label: `${counts.gone} Gone`,     bg: "#e53935", color: "#fff" },
                        ].map(chip => counts[chip.key] > 0 && (
                          <span key={chip.key} style={{ background: chip.bg, color: chip.color,
                            borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600 }}>
                            {chip.label}
                          </span>
                        ))}
                      </div>
                      <DiffTable diffRows={diffRows} keywordTotals={results.keyword_totals || {}} />
                    </>
                  );
                })()}

                {(!compareBaseline || !diffView) && (() => {
                  const kwNames = Object.keys(results.keyword_totals || {});
                  const multiKw = kwNames.length > 1;
                  // Bars are scaled to the busiest page across the whole result,
                  // not just this page of the table.
                  const maxOf = (get) => Math.max(1, ...results.table.map(get));
                  const maxTotal = maxOf(r => r.total_count || 0);
                  const maxByKw = Object.fromEntries(kwNames.map(kw => [kw, maxOf(r => r.keywords?.[kw]?.count || 0)]));
                  const countCell = (n, max) => (
                    <div className="r-count">
                      <span className="r-bar"><i style={{ width: `${Math.max(2, (n / max) * 100)}%` }} /></span>
                      <b>{n.toLocaleString()}</b>
                    </div>
                  );
                  return (
                <>
                <div className="r-table-wrap">
                  <div className="r-table-scroll">
                  <table className="r-table">
                    <thead>
                      <tr>
                        <th>Page</th>
                        {multiKw
                          ? kwNames.map(kw => <th key={kw} className="r-num">{kw}</th>)
                          : <th className="r-num">Matches</th>
                        }
                        <th className="r-ctx-col"><span className="visually-hidden">Context</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.map((row, index) => {
                        const { host, path } = splitPageUrl(row.page);
                        const hasSnippets = row.keywords && Object.values(row.keywords).some(k => k.snippets?.length > 0);
                        const isOpen = !!expandedRows[row.page];
                        return (
                        <React.Fragment key={index}>
                          <tr className="r-row">
                            <td>
                              <a className="r-url" href={row.page} target="_blank" rel="noopener noreferrer">
                                <span className="r-url-host">{host}</span><span className="r-url-path">{path}</span>
                              </a>
                            </td>
                            {multiKw
                              ? kwNames.map(kw => (
                                  <td key={kw} className="r-count-cell">{countCell(row.keywords?.[kw]?.count ?? 0, maxByKw[kw])}</td>
                                ))
                              : <td className="r-count-cell">{countCell(row.total_count || 0, maxTotal)}</td>
                            }
                            <td className="r-ctx-col">
                              {hasSnippets && (
                                <button
                                  type="button"
                                  className="r-ctx-btn"
                                  aria-expanded={isOpen}
                                  onClick={() => toggleRow(row.page)}
                                >
                                  {isOpen ? "Hide" : "Context"}
                                  <ResultIcon name="chevron" />
                                </button>
                              )}
                            </td>
                          </tr>
                          {isOpen && row.keywords && (
                            <tr className="r-detail">
                              <td colSpan={(multiKw ? kwNames.length : 1) + 2}>
                                {row.filters_applied && Object.keys(row.filters_applied).length > 0 && (
                                  <div className="r-filters">
                                    <strong>Found under filter:</strong>{" "}
                                    {Object.entries(row.filters_applied).map(([label, value], i, arr) => (
                                      <span key={label}>
                                        {label} = {value}{i < arr.length - 1 ? ", " : ""}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {Object.entries(row.keywords).map(([kw, kdata]) =>
                                  kdata.snippets?.length > 0 && (
                                    <div key={kw} className="r-kw-block">
                                      <div className="r-kw-head">
                                        <b>{kw}</b> · {kdata.snippets.length === kdata.count
                                          ? `${kdata.count} match${kdata.count === 1 ? "" : "es"}`
                                          : `${kdata.snippets.length} of ${kdata.count} matches shown`}
                                      </div>
                                      <ol className="r-snippets">
                                        {kdata.snippets.map((snippet, si) => (
                                          <li key={si}><span>{highlightKeyword(snippet, kw)}</span></li>
                                        ))}
                                      </ol>
                                    </div>
                                  )
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="r-pager">
                    <div className="r-pager-group">
                      <button type="button" className="r-page-btn" disabled={currentPage === 1} onClick={() => handlePageChange(1)}>« First</button>
                      <button type="button" className="r-page-btn" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>‹ Previous</button>
                    </div>
                    <span>Page {currentPage} of {totalPages}</span>
                    <div className="r-pager-group">
                      <button type="button" className="r-page-btn" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next ›</button>
                      <button type="button" className="r-page-btn" disabled={currentPage === totalPages} onClick={() => handlePageChange(totalPages)}>Last »</button>
                    </div>
                  </div>
                )}
                </>
                  );
                })()}

                {/* Not Found pages panel */}
                {showNotFound && user?.plan === "pro" && (() => {
                  const foundSet = new Set(results.table.map(r => r.page));
                  const notFoundPages = (results.scanned_pages || []).filter(u => !foundSet.has(u)).sort();
                  return (
                    <div className="r-notfound">
                      <div className="r-notfound-head">
                        <h6>Pages with no keyword match</h6>
                        <span>
                          {notFoundPages.length} page{notFoundPages.length !== 1 ? "s" : ""} scanned but keyword not found
                        </span>
                      </div>
                      {notFoundPages.length === 0 ? (
                        <p className="r-muted">All scanned pages contained at least one keyword match.</p>
                      ) : (
                        <div className="r-table-wrap">
                          <div className="r-table-scroll">
                          <table className="r-table">
                            <thead>
                              <tr>
                                <th className="r-num-col">#</th>
                                <th>Page</th>
                              </tr>
                            </thead>
                            <tbody>
                              {notFoundPages.map((url, i) => {
                                const { host, path } = splitPageUrl(url);
                                return (
                                  <tr key={i} className="r-row">
                                    <td className="r-num-col r-muted">{i + 1}</td>
                                    <td>
                                      <a className="r-url" href={url} target="_blank" rel="noopener noreferrer">
                                        <span className="r-url-host">{host}</span><span className="r-url-path">{path}</span>
                                      </a>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : results && results.table.length === 0 ? (
              results.site_unreachable ? (
                unreachableCard(results)
              ) : (
              <div className="results-area">
                <div className="r-notices">
                  {renderedBanner(results)}
                  {failedNotice(results)}
                  {results.dynamic_pages_capped && (
                    <ResultNotice kind="warn" icon="alert">
                      This site uses JavaScript rendering, so we scanned the first {results.max_pages} pages.{" "}
                      <a href="#pricing" onClick={(e) => { e.preventDefault(); setUpgradeReason("dynamic_pages"); setShowUpgradeModal(true); }}>
                        Upgrade to Pro
                      </a>{" "}
                      to scan every page of dynamic websites.
                    </ResultNotice>
                  )}
                </div>
                {noMatchesPanel(results)}
              </div>
              )
            ) : null}

            {currentSectionIndex > 0 && (
              <div
                className="scroll-up-wrapper"
                onClick={() => scrollToSection("up")}
              >
                <button className="scroll-up-button" aria-label="Scroll up">
                  <span></span>
                </button>
              </div>
            )}

            {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
              <div
                className="scroll-wrapper"
                onClick={() => scrollToSection("down")}
              >
                <button className="scroll-down-button" aria-label="Scroll down">
                  <span></span>
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section id="whatiswowowl" className="about-section py-5 full-section">
        <div className="container">
          <p className="section-eyebrow text-center">About the tool</p>
          <h2 className="about-heading text-center mb-3">What is WowOwl?</h2>
          <div className="mx-auto" style={{ maxWidth: "820px" }}>
            <p className="about-text text-center">
              WowOwl is a website-wide keyword scanning tool that helps you find any word or phrase across an entire website. Enter a URL and your keywords, and WowOwl crawls every page it can reach — following the sitemap first — to show exactly where each keyword appears, how many times, and in what context. Unlike your browser's Ctrl+F, which only searches the page you have open, WowOwl searches the entire website in a single scan.
            </p>

            <div className="wowowl-steps">
              {[
                { title: "Enter a URL and keywords", desc: "No sign-up needed to try it" },
                { title: "WowOwl crawls the site", desc: "Sitemap-first, with dynamic rendering for JavaScript-heavy pages" },
                { title: "Get a sortable report", desc: "Page URL, match count, and highlighted snippets — exportable as CSV or PDF" },
              ].map((step, i) => (
                <div className="wowowl-step" key={step.title}>
                  <div className="wowowl-step-num">{i + 1}</div>
                  <h5>{step.title}</h5>
                  <p>{step.desc}</p>
                </div>
              ))}
            </div>

            <h3 className="wowowl-subheading text-center">Who uses WowOwl</h3>
            <div className="wowowl-persona-list">
              {[
                { title: "SEO professionals and agencies", desc: "Audit keyword usage, review topical coverage, find outdated content, and prepare website reports for clients." },
                { title: "Content and marketing teams", desc: "Check how consistently brand names, product names, campaign messages, and important terminology appear across a website." },
                { title: "Website owners", desc: "Find old prices, phone numbers, email addresses, company names, dates, or other information that needs to be updated." },
                { title: "Legal and compliance teams", desc: "Verify whether required disclaimers, legal statements, and approved wording appear across the correct website pages." },
                { title: "Researchers and job seekers", desc: "Search organisations, universities, government websites, and company career pages for specific words, roles, technologies, or topics." },
              ].map((p) => (
                <div className="wowowl-persona-row" key={p.title}>
                  <p className="wowowl-persona-title">{p.title}</p>
                  <p className="wowowl-persona-desc">{p.desc}</p>
                </div>
              ))}
            </div>

            <div className="wowowl-cta-banner">
              <p>Free to start — no account needed. Pro unlocks multi-keyword search, regex matching, path filters, scheduled scans, and more.</p>
              <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }); }}>
                See pricing →
              </a>
            </div>
          </div>
        </div>
        {currentSectionIndex > 0 && (
          <div className="scroll-up-wrapper" onClick={() => scrollToSection("up")}>
            <button className="scroll-up-button" aria-label="Scroll up"><span></span></button>
          </div>
        )}
        {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
          <div className="scroll-wrapper" onClick={() => scrollToSection("down")}>
            <button className="scroll-down-button" aria-label="Scroll down"><span></span></button>
          </div>
        )}
      </section>

      <section id="features" className="features-section full-section">
        <p className="section-eyebrow">Capabilities</p>
        <h2 className="wowowl-heading">
          Everything WowOwl can do.
        </h2>
        <p className="features-subtext">
          From keyword tracking to real-time analysis — understand web content at a glance.
        </p>
        <div className="features-card mx-auto">
          <div className="features-grid" ref={featuresScrollRef} onScroll={handleFeaturesScroll}>
            {featureItems.map((item, i) => (
              <div
                className="feature-item"
                key={item.title}
                ref={(el) => (featureCardRefs.current[i] = el)}
              >
                <span className="feature-icon">{item.icon}</span>
                <h5>{item.title}</h5>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="features-dots">
            {featureItems.map((item, i) => (
              <button
                key={item.title}
                type="button"
                className={`features-dot ${i === activeFeatureIndex ? "active" : ""}`}
                aria-label={`Go to ${item.title}`}
                onClick={() => scrollToFeature(i)}
              />
            ))}
          </div>
        </div>
        {currentSectionIndex > 0 && (
              <div
                className="scroll-up-wrapper"
                onClick={() => scrollToSection("up")}
              >
                <button className="scroll-up-button" aria-label="Scroll up">
                  <span></span>
                </button>
              </div>
            )}

            {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
              <div
                className="scroll-wrapper"
                onClick={() => scrollToSection("down")}
              >
                <button className="scroll-down-button" aria-label="Scroll down">
                  <span></span>
                </button>
              </div>
            )}
      </section>

      <section id="about" className="about-section py-5 full-section">
        <div className="container">
          <div className="row align-items-center">
            {/* Left: Story */}
            <div
              className="col-md-7 mb-4 mb-md-0 wow fadeInLeft"
              data-wow-delay="0.2s"
            >
              <p className="section-eyebrow">The maker</p>
              <h2 className="about-heading mb-3">
                Meet the creator.
              </h2>
              <p className="about-text">
                It started with a job. Then another one. And another. <br />
                <br />
                Every time, I found myself doing the same frustrating thing —
                trying to find one specific word buried somewhere in an
                employer’s enormous website. Not one page, not ten — but
                hundreds. I’d manually click through links, try in-site search
                boxes that never worked, and even resorted to crawling sitemaps
                like a detective on caffeine.
                <br />
                <br />
                After the third or fourth time, I thought, “Why isn’t there a
                simple tool for this?”
                <br />
                <br />
                So I built it.
                <br />
                <br />
                I’m Aniket from Mumbai, India — and WowOwl is the tool I wish I
                had back then. If you’ve ever felt stuck trying to{" "}
                <em>“just find that one word,”</em> I built this for you too.
              </p>
            </div>

            {/* Right: Picture */}
            <div
              className="col-md-5 text-center wow fadeInRight"
              data-wow-delay="0.4s"
            >
              <img
                src={creatorImg}
                alt="Aniket - Creator of WowOwl"
                className="creator-img img-fluid"
              />
            </div>
          </div>
        </div>
        {currentSectionIndex > 0 && (
              <div
                className="scroll-up-wrapper"
                onClick={() => scrollToSection("up")}
              >
                <button className="scroll-up-button" aria-label="Scroll up">
                  <span></span>
                </button>
              </div>
            )}

            {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
              <div
                className="scroll-wrapper"
                onClick={() => scrollToSection("down")}
              >
                <button className="scroll-down-button" aria-label="Scroll down">
                  <span></span>
                </button>
              </div>
            )}
      </section>

      <section id="faqs" className="faq-section py-5 full-section">
        <div className="container">
          <p className="section-eyebrow text-center">Questions</p>
          <h2 className="mb-4 text-center">
            Frequently asked.
          </h2>
          <div className="accordion" id="faqAccordion">
            {[
              {
                q: "What is WowOwl?",
                a: "WowOwl is a website content scanner that helps you find specific words across all pages of a site by just entering the URL and a keyword.",
              },
              {
                q: "How to use WowOwl?",
                a: "Enter the website URL and the keyword you want to find. Click ‘Scan’ and let WowOwl do the crawling. It will show you where and how often your keyword appears.",
              },
              {
                q: "How does it work?",
                a: "WowOwl crawls public web pages starting from the provided URL, collects the content, and searches for the keyword using intelligent word-matching logic.",
              },
              {
                q: "Does WowOwl scan all websites?",
                a: "It scans most public websites. However, sites that block bots or restrict crawlers (via robots.txt or firewalls) may not be fully scanned.",
              },
              {
                q: "Who can use WowOwl?",
                a: "Anyone! It’s designed for content creators, SEO experts, students, job seekers, and anyone curious about how content is distributed across a website.",
              },
              {
                q: "Is the scan result real-time?",
                a: "Yes, the scanning and results are generated in real-time when you run a scan.",
              },
              {
                q: "Do I need to install anything?",
                a: "No installation needed. WowOwl is a web-based tool — just visit the site and start scanning.",
              },
            ].map((item, index) => (
              <div className="accordion-item" key={index}>
                <h2 className="accordion-header" id={`heading${index}`}>
                  <button
                    className={`accordion-button ${
                      index !== 0 ? "collapsed" : ""
                    }`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse${index}`}
                    aria-expanded={index === 0 ? "true" : "false"}
                    aria-controls={`collapse${index}`}
                  >
                    {item.q}
                  </button>
                </h2>
                <div
                  id={`collapse${index}`}
                  className={`accordion-collapse collapse ${
                    index === 0 ? "show" : ""
                  }`}
                  aria-labelledby={`heading${index}`}
                  data-bs-parent="#faqAccordion"
                >
                  <div className="accordion-body">{item.a}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {currentSectionIndex > 0 && (
              <div
                className="scroll-up-wrapper"
                onClick={() => scrollToSection("up")}
              >
                <button className="scroll-up-button" aria-label="Scroll up">
                  <span></span>
                </button>
              </div>
            )}

            {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
              <div
                className="scroll-wrapper"
                onClick={() => scrollToSection("down")}
              >
                <button className="scroll-down-button" aria-label="Scroll down">
                  <span></span>
                </button>
              </div>
            )}
      </section>

      <section id="blog" className="blog-section full-section py-5">
        <div className="container">
          <p className="section-eyebrow text-center">Writing</p>
          <h2 className="text-center mb-2">
            From the blog.
          </h2>
          <p className="text-center mb-5" style={{ color: "#666" }}>
            Guides for SEO professionals, content writers, and researchers
          </p>
          <div className="row g-4">
            {blogPosts.posts.map((post, i) => (
              <div className="col-md-4" key={i}>
                <a href={post.url} className="blog-card text-decoration-none">
                  <div className="blog-card-inner">
                    <p className="blog-read-time">{post.readTime}</p>
                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <span className="blog-read-more">Read article →</span>
                  </div>
                </a>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <a href={blogPosts.indexUrl} style={{
              display: "inline-block", padding: "12px 28px", borderRadius: "10px",
              background: "var(--accent, #4caf50)", color: "var(--on-accent, #fff)", fontWeight: 700,
              textDecoration: "none", fontSize: "14px",
            }}>
              More blogs →
            </a>
          </div>
        </div>
        {currentSectionIndex > 0 && (
          <div className="scroll-up-wrapper" onClick={() => scrollToSection("up")}>
            <button className="scroll-up-button" aria-label="Scroll up"><span></span></button>
          </div>
        )}
        {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
          <div className="scroll-wrapper" onClick={() => scrollToSection("down")}>
            <button className="scroll-down-button" aria-label="Scroll down"><span></span></button>
          </div>
        )}
      </section>

      {/* Hidden for Pro subscribers — showing an "upgrade to Pro" pricing table
          to someone who already pays for it is the most jarring case of these
          marketing sections; the others (Blog/FAQ/About) stay visible since
          they're still plausibly useful to existing customers. */}
      {user?.plan !== "pro" && (
      <>
      {/* Editorial: transparent, letting the body's single, correctly
          viewport-anchored ambient glow show through — duplicating the
          gradient here too seamed at the section boundary and drifted on
          scroll (background-attachment: fixed only behaves seamlessly on
          the root/body, not arbitrary descendant elements). Classic keeps
          its original var(--page-bg, #f8faf8), unaffected either way since
          it never defines that token or the glow variable. */}
      <section id="pricing" className="full-section py-5" style={{ background: "transparent" }}>
        <div className="container" style={{ maxWidth: "1060px" }}>
          <p className="section-eyebrow text-center">Pricing</p>
          <h2 className="text-center mb-2">
            Simple, honest pricing.
          </h2>
          <p className="text-center mb-5" style={{ color: "var(--ink-soft, #666)" }}>
            Start free. Sign in for more. Go Pro for everything.
          </p>

          {(() => {
            /* Was hardcoded to editorial's LIGHT accent (#0f7a54) always, so
               even in dark mode the Pro border/badge/checkmarks stayed
               forest-green instead of picking up the theme's mint accent —
               var(--accent) already resolves correctly per mode. */
            const pricingAccent = "var(--accent, #0f7a54)";
            const pricingAccentShadow = "var(--accent-shadow, 15,122,84)";
            const check = (color = pricingAccent) => <span style={{ color, fontWeight: 700, flexShrink: 0 }}>✓</span>;
            const cross = () => <span style={{ color: "var(--muted, #ccc)", flexShrink: 0 }}>✕</span>;
            /* cardBase.background was a hardcoded "#fff" — every price,
               label, and border below was written assuming that white card,
               so none of it adapted for dark mode even though .pricing-card
               itself already gets a dark background via CSS. Swapping the
               whole block to var(--token, lightModeValue) fixes it for both
               modes from the same values instead of branching per theme. */
            const cardBase = { borderRadius: "16px", padding: "28px 22px", background: "var(--card, #fff)", display: "flex", flexDirection: "column" };
            const cardClass = "pricing-card";
            return (
              <div className="pricing-grid">

                {/* Free — guest and signed-in, side by side in one card */}
                <div className={cardClass} style={{ ...cardBase, border: "1.5px solid var(--border, #e0e0e0)" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--muted, #aaa)", letterSpacing: "0.08em", marginBottom: "6px" }}>FREE</div>
                  <div style={{ fontSize: "30px", fontWeight: 800, color: "var(--ink, #222)", marginBottom: "2px" }}>{getLocalizedFreeLabel(pricingTable, visitorCountryCode) || "₹0"}</div>
                  <div style={{ fontSize: "12px", color: "var(--muted, #bbb)", marginBottom: "20px" }}>No sign-up needed — sign in for more</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "16px" }}>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--muted, #999)", letterSpacing: "0.06em", marginBottom: "8px" }}>NOT SIGNED IN</div>
                      {["1 scan / day", "100 pages max", "Local history"].map((f, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px", fontSize: "12px", color: "var(--ink-soft, #444)" }}>
                          {check()} {f}
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: "#66bb6a", letterSpacing: "0.06em", marginBottom: "8px" }}>SIGNED IN</div>
                      {["2 scans / day", "200 pages max", "Perpetual history"].map((f, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px", fontSize: "12px", color: "var(--ink-soft, #444)" }}>
                          {check("#66bb6a")} {f}
                        </div>
                      ))}
                    </div>
                  </div>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 auto" }}>
                    {[
                      ["Single keyword & exact match", true],
                      ["Multi-keyword, regex, path filters", false],
                      ["Scheduled scans", false],
                    ].map(([label, on], i) => (
                      <li key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", fontSize: "13px", color: on ? "var(--ink-soft, #444)" : "var(--muted, #ccc)" }}>
                        {on ? check() : cross()} {label}
                      </li>
                    ))}
                  </ul>
                  {user ? (
                    <button style={{ marginTop: "20px", width: "100%", padding: "11px", borderRadius: "10px",
                      border: "1.5px solid #66bb6a", background: "transparent", color: "#66bb6a",
                      fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
                      onClick={() => document.getElementById("scan")?.scrollIntoView({ behavior: "smooth" })}>
                      Go to scanner
                    </button>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "20px" }}>
                      <button style={{ width: "100%", padding: "11px", borderRadius: "10px",
                        border: "1.5px solid var(--border, #e0e0e0)", background: "transparent", color: "var(--muted, #888)",
                        fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
                        onClick={() => document.getElementById("scan")?.scrollIntoView({ behavior: "smooth" })}>
                        Scan once →
                      </button>
                      <button style={{ width: "100%", padding: "11px", borderRadius: "10px",
                        border: "1.5px solid #66bb6a", background: "transparent", color: "#66bb6a",
                        fontWeight: 700, fontSize: "13px", cursor: "pointer" }}
                        onClick={handleLogin}>
                        Sign in for 2 scans →
                      </button>
                    </div>
                  )}
                </div>

                {/* One-timer — both packs in one card, distinct warm accent so it
                    reads as "a different kind of purchase" rather than another
                    subscription-style tier competing with Free/Pro. */}
                <div className={cardClass} style={{ ...cardBase, border: "2px solid #c17a3f", position: "relative" }}>
                  <div style={{ position: "absolute", top: "-14px", left: "50%", transform: "translateX(-50%)",
                    background: "#c17a3f", color: "#fff", borderRadius: "20px",
                    padding: "3px 16px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", whiteSpace: "nowrap",
                  }}>NO SUBSCRIPTION</div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#c17a3f", letterSpacing: "0.08em", marginBottom: "6px", marginTop: "6px" }}>ONE-TIMER</div>
                  <div style={{ fontSize: "13px", color: "var(--muted, #999)", marginBottom: "20px" }}>Pay once, scan like Pro — no recurring charge</div>

                  {[
                    { tier: "starter", scans: 5, price: 49, days: 10 },
                    { tier: "value", scans: 15, price: 99, days: 30 },
                  ].map(({ tier, scans, price, days }) => {
                    const displayPrice = getLocalizedPrice(pricingTable, visitorCountryCode, tier) || `₹${price}`;
                    const perScanDisplay = getLocalizedPricePerUnit(pricingTable, visitorCountryCode, tier, scans)
                      || `₹${(price / scans).toFixed(2)}/scan`;
                    return (
                      <div key={tier} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                        border: "1px solid var(--border, #e0e0e0)", borderRadius: "10px", padding: "12px 14px", marginBottom: "10px" }}>
                        <div>
                          <div style={{ fontSize: "14px", fontWeight: 700, color: "var(--ink, #222)" }}>{scans} scans</div>
                          <div style={{ fontSize: "11px", color: "var(--muted, #999)" }}>{days}-day credits</div>
                          <div style={{ fontSize: "11px", color: "#c17a3f", fontWeight: 700, marginTop: "2px" }}>{perScanDisplay}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{ fontSize: "24px", fontWeight: 800, color: "var(--ink, #222)" }}>{displayPrice}</div>
                          <button
                            className="pricing-cta-btn"
                            style={{ padding: "7px 14px", borderRadius: "8px", border: "none",
                              background: "#c17a3f", color: "#fff", fontWeight: 700, fontSize: "12px", cursor: "pointer" }}
                            onClick={() => handleBuyCredits(tier)}>
                            Buy →
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  <ul style={{ listStyle: "none", padding: 0, margin: "8px 0 auto" }}>
                    {["Multi-keyword, regex & path filters", "Up to 3,000 pages per scan"].map((f, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", fontSize: "13px", color: "var(--ink-soft, #333)" }}>
                        {check("#c17a3f")} {f}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Pro */}
                <div className={cardClass} style={{ ...cardBase, border: `2px solid ${pricingAccent}`,
                  background: "linear-gradient(160deg, var(--accent-wash, #f1faf1) 0%, var(--card, #fff) 100%)", position: "relative" }}>
                  <div style={{ position: "absolute", top: "-14px", left: "50%", transform: "translateX(-50%)",
                    background: pricingAccent, color: "var(--on-accent, #fff)", borderRadius: "20px",
                    padding: "3px 16px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", whiteSpace: "nowrap",
                  }}>MOST POPULAR</div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: pricingAccent, letterSpacing: "0.08em", marginBottom: "6px" }}>PRO</div>
                  <div style={{ fontSize: "30px", fontWeight: 800, color: "var(--ink, #222)", marginBottom: "2px" }}>
                    {getLocalizedPrice(pricingTable, visitorCountryCode, "pro") || "₹149"}<span style={{ fontSize: "15px", fontWeight: 500, color: "var(--muted, #888)" }}> / month</span>
                  </div>
                  <div style={{ fontSize: "12px", color: "var(--muted, #bbb)", marginBottom: "20px" }}>Cancel anytime</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 auto" }}>
                    {[
                      "100 scans per month",
                      "Up to 3,000 pages per scan",
                      "Multi-keyword",
                      "Exact match & regex search",
                      "Path filters & exclude paths",
                      "Perpetual scan history",
                      "Share results link",
                      "CSV & PDF export",
                      "Before/after comparison",
                      "Bulk URL scan",
                      "Scheduled recurring scans (up to 20)",
                    ].map((f, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", fontSize: "13px", color: "var(--ink-soft, #333)" }}>
                        {check()} {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    className="pricing-cta-btn"
                    style={{ marginTop: "20px", width: "100%", padding: "11px", borderRadius: "10px",
                      border: "none", background: pricingAccent, color: "var(--on-accent, #fff)",
                      fontWeight: 700, fontSize: "13px", cursor: "pointer",
                      boxShadow: `0 4px 14px rgba(${pricingAccentShadow},0.3)` }}
                    onClick={() => {
                      if (user && user.plan === "pro") return;
                      if (!user) { handleLogin(); return; }
                      handleUpgrade();
                    }}>
                    {user && user.plan === "pro" ? "You're on Pro ✓" : "Upgrade to Pro →"}
                  </button>
                </div>

              </div>
            );
          })()}

          <p className="text-center mt-4" style={{ fontSize: "13px", color: "var(--muted, #aaa)" }}>
            Need a custom plan for your team? <a href="#contact" style={{ color: "var(--accent, #4caf50)" }}>Get in touch</a>.
          </p>
          {/* pricingTable (from GET /api/pricing) mirrors Dodo's own country-override
              config live — a visitor in one of those countries sees the exact
              localized price above, no disclaimer needed. Anyone else still sees
              the plain INR price, which is why this note only shows for them. */}
          {!pricingTable[visitorCountryCode] && (
            <p className="text-center" style={{ fontSize: "11px", color: "var(--muted, #bbb)", marginTop: "4px" }}>
              Prices shown in INR — your checkout price may vary by region.
            </p>
          )}
        </div>
        {currentSectionIndex > 0 && (
          <div className="scroll-up-wrapper" onClick={() => scrollToSection("up")}>
            <button className="scroll-up-button" aria-label="Scroll up"><span></span></button>
          </div>
        )}
        {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
          <div className="scroll-wrapper" onClick={() => scrollToSection("down")}>
            <button className="scroll-down-button" aria-label="Scroll down"><span></span></button>
          </div>
        )}
      </section>
      </>
      )}

      <section id="contact" className="contact-section  full-section">
        <div className="container">
          <p className="section-eyebrow text-center">Contact</p>
          <h2 className="mb-4 text-center">
            {"We'd love to hear from you."}
          </h2>
          <ContactForm />
        </div>
        {currentSectionIndex > 0 && (
              <div
                className="scroll-up-wrapper"
                onClick={() => scrollToSection("up")}
              >
                <button className="scroll-up-button" aria-label="Scroll up">
                  <span></span>
                </button>
              </div>
            )}

            {currentSectionIndex < sectionIds.length - 1 && currentSectionIndex !== 0 && (
              <div
                className="scroll-wrapper"
                onClick={() => scrollToSection("down")}
              >
                <button className="scroll-down-button" aria-label="Scroll down">
                  <span></span>
                </button>
              </div>
            )}
      </section>
      <footer className="site-footer">
        <div className="container">
          <div className="row gy-3">
            <div className="col-md-3">
              <strong>🦉 WowOwl</strong>
              <p>Find any word across an entire website. Free, fast, no sign-up.</p>
            </div>
            <div className="col-md-3">
              <strong>Blog</strong>
              <ul className="footer-links">
                {blogPosts.posts.map((post) => (
                  <li key={post.url}><a href={post.url}>{post.title}</a></li>
                ))}
                {blogPosts.total > blogPosts.posts.length && (
                  <li><a href={blogPosts.indexUrl}>View all articles →</a></li>
                )}
              </ul>
            </div>
            <div className="col-md-3">
              <strong>Links</strong>
              <ul className="footer-links">
                <li><a href="#scan">Scan a website</a></li>
                {user?.plan !== "pro" && <li><a href="#pricing">Pricing</a></li>}
                <li><a href="#faqs">FAQs</a></li>
                <li><a href="#contact">Contact</a></li>
              </ul>
            </div>
            <div className="col-md-3">
              <strong>Legal</strong>
              <ul className="footer-links">
                <li><a href="/privacy-policy.html">Privacy Policy</a></li>
                <li><a href="/terms-of-service.html">Terms of Service</a></li>
                <li><a href="/refund-policy.html">Refund &amp; Cancellation Policy</a></li>
              </ul>
            </div>
          </div>
          <hr />
          <p className="footer-copy">
            © {new Date().getFullYear()} WowOwl · Built by Aniket, Mumbai 🇮🇳
          </p>
        </div>
      </footer>
    </>
  );
}

export default App;
